import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Map as MapIcon,
  Plus,
  Plane,
  Hotel,
  Bus,
  Shield,
  Package,
  Car,
  FileText,
  Ship,
  Pencil,
  Trash2,
  Copy,
  RefreshCw,
  ChevronDown,
  ArrowRight,
  Clock,
  MapPin,
  Users,
  AlertTriangle,
  X,
  Check,
  DollarSign,
  FileCheck,
  RotateCcw,
  MoreVertical,
  ChevronRight,
  Tag,
  Calendar,
  Percent,
  Globe,
  Bed,
  Moon,
  Building2,
} from "lucide-react";
import BaseModal from "../../../components/BaseComponents/BaseModal/BaseModal";
import { useToast } from "../../../context/ToastContext";
import {
  useCopyProduct,
  useValidateProductRemoval,
  useDeleteBookingProduct,
  useCreateProductModification,
  useCreateScheduleChange,
  useGetScheduleChanges,
  useAddBookingSSRs,
  useDeleteBookingSSR,
  useUpdateSSRStatus,
  useGetCancellationCases,
} from "../../../api/booking/useBookingApi";
import { SSR_STATUS_VALUES, ssrStatusColor } from "../../../constants/documentConstants";
import { useGetAirports, getAirports } from "../../../api/geo/useAirports";
import { getAirlines } from "../../../api/products/useAirlinesApi";
import { useGetSSRCategories, useGetSSRItems } from "../../../api/products/useSSRApi";
import { useCheckScheduleApi } from "../../../api/products/useScheduleUpdaterApi";
import { SSR_CATEGORIES } from "../../../components/Booking/StepSSR";
import SearchableSelect from "../../../components/BaseComponents/SearchableSelect/SearchableSelect";
import BoardCodeDropdown from "../../../components/Booking/BoardCodeDropdown";
import FlightRoute from "../../../components/Booking/FlightRoute";
import SearchableListDropdown from "../../../components/Booking/SearchableListDropdown";
import { BOARD_CODE_TO_NAME, ROOM_TYPES_FULL, ROOM_CHARS_FULL } from "../../../components/Booking/bookingConstants";
import styles from "./ItineraryTab.module.css";

/* ── Helpers ── */
const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};
const formatDateShort = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
};
const formatTime = (t) => t || "—";
const calcDuration = (depTime, arrTime) => {
  if (!depTime || !arrTime) return null;
  try {
    const [dh, dm] = depTime.split(":").map(Number);
    const [ah, am] = arrTime.split(":").map(Number);
    let diff = (ah * 60 + am) - (dh * 60 + dm);
    if (diff < 0) diff += 24 * 60; // next day arrival
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return h > 0 ? `${h}h ${m > 0 ? `${m}m` : ""}`.trim() : `${m}m`;
  } catch { return null; }
};
/* A return leg that departs before the last outbound leg: almost always a typed wrong date
   (5 Nov 2026: return 03/04/2026 for an outbound on 26/03/2027). Same rule as the backend's
   flightLegs.returnBeforeOutbound; the backend saves legs outbound-first. */
const legKey = (l) => {
  const dt = String(l?.departureDate || l?.date || l?.departure || "");
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(dt) || (/^(\d{2})[./](\d{2})[./](\d{4})/.exec(dt) && [null, dt.slice(6, 10), dt.slice(3, 5), dt.slice(0, 2)]);
  return iso ? `${iso[1]}-${iso[2]}-${iso[3]}T${String(l?.departureTime || l?.time || "00:00").slice(0, 5)}` : "";
};
const isReturnLeg = (l) => /^(inbound|return|ret|in)$/i.test(String(l?.direction || "").trim());
const returnBeforeOutbound = (legs = []) => {
  const out = legs.filter((l) => !isReturnLeg(l)).map(legKey).filter(Boolean).sort();
  const ins = legs.filter(isReturnLeg).map(legKey).filter(Boolean).sort();
  return !!(out.length && ins.length && ins[0] < out[out.length - 1]);
};

const FLIGHT_TYPE_LABELS = { OW: "One Way", RT: "Round Trip", MULTI: "Multi City" };
const PRICING_MODE_LABELS = { FULL: "Full Flight", SPLIT: "Split Flight" };

const ICON_MAP = {
  Flight: Plane, Hotel: Hotel, Transfer: Bus, Insurance: Shield,
  Package: Package, CarRental: Car, Visa: FileText, Cruise: Ship,
};

const CopyableValue = ({ label, value, showToast }) => {
  if (!value || value === "—") return <span className={styles.infoValue}>—</span>;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      showToast(`${label} copied`, "success");
    }).catch(() => {
      showToast(`Failed to copy ${label}`, "error");
    });
  };
  return (
    <span className={styles.infoValue} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {value}
      <button
        type="button"
        onClick={handleCopy}
        title={`Copy ${label}`}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "inline-flex", alignItems: "center", color: "#6b7280", borderRadius: 3 }}
        onMouseEnter={(e) => e.currentTarget.style.color = "#2563eb"}
        onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
      >
        <Copy size={12} />
      </button>
    </span>
  );
};

/* PNR segment chip with copy-to-clipboard — mirrors the Supplier Ref copy option. */
const CopyablePnrChip = ({ pnr, showToast }) => {
  if (!pnr) return null;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(pnr)
      .then(() => showToast?.("PNR copied", "success"))
      .catch(() => showToast?.("Failed to copy PNR", "error"));
  };
  return (
    <div className={styles.segChip}>
      <span className={styles.segChipLabel}>PNR</span>
      <span className={styles.segChipCode}>{pnr}</span>
      <button
        type="button"
        onClick={handleCopy}
        title="Copy PNR"
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginLeft: 1, display: "inline-flex", alignItems: "center", color: "#6b7280", borderRadius: 3 }}
        onMouseEnter={(e) => e.currentTarget.style.color = "#2563eb"}
        onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
      >
        <Copy size={11} />
      </button>
    </div>
  );
};

/* ── Shared: Click-outside hook ── */
const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (e) => {
      if (ref.current && !ref.current.contains(e.target)) handler();
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref, handler]);
};

/* ══════════════════════════════════════════════
   SHARED UNIFIED CARD PRIMITIVES
   One neutral structure for every product type.
   Only the status text is coloured (Confirmed /
   Pending / Cancelled).  (Client mockup spec)
   ══════════════════════════════════════════════ */

/* Coloured status pill — the only place colour is used.
   Cancellation engine adds the standardised "Active" / "Partially Cancelled"
   states on top of the operational Confirmed / Pending / Cancelled set. */
const STATUS_CLASS_KEY = {
  Confirmed: "Confirmed", Pending: "Pending", Cancelled: "Cancelled",
  Active: "Active", "Partially Cancelled": "Partial", "Partially Confirmed": "Partial", "Not booked": "Cancelled",
};
const StatusPill = ({ status }) => {
  const label = status || "Pending";
  const key = STATUS_CLASS_KEY[label] || "Pending";
  return (
    <span className={`${styles.uStatus} ${styles[`uStatus${key}`]}`}>
      <span className={styles.uStatusDot} />
      {label}
    </span>
  );
};

/* Map a product type label → its icon-badge accent class */
const ICON_ACCENT_CLASS = {
  Flight: "uIconFlight", Hotel: "uIconHotel", Transfer: "uIconTransfer",
  Insurance: "uIconInsurance", Package: "uIconPackage", CarRental: "uIconCarRental",
  Visa: "uIconVisa", Cruise: "uIconCruise",
};

/* Map a product type label → its card-level accent class (sets accent vars) */
const CARD_ACCENT_CLASS = {
  Flight: "uCardFlight", Hotel: "uCardHotel", Transfer: "uCardTransfer",
  Insurance: "uCardInsurance", Package: "uCardPackage", CarRental: "uCardCarRental",
  Visa: "uCardVisa", Cruise: "uCardCruise",
};

/* Card header: icon + type + status (left), action buttons (right).
   When `canc` is supplied, the standardised cancellation case ref is shown. */
const CardHead = ({ icon, type, status, right, accentType, canc, showToast }) => (
  <div className={styles.uHead}>
    <div className={styles.uHeadL}>
      <div className={`${styles.uIcon} ${styles[ICON_ACCENT_CLASS[accentType]] || styles.uIconOther}`}>{icon}</div>
      <span className={styles.uType}>{type}</span>
      <StatusPill status={status} />
      <CancCaseRef canc={canc} showToast={showToast} />
    </div>
    <div className={styles.uHeadR}>{right}</div>
  </div>
);

/* Square icon action button (pencil / menu) */
const IconActionBtn = ({ icon, title, onClick, innerRef }) => (
  <button type="button" className={styles.uActBtn} title={title} onClick={onClick} ref={innerRef}>
    {icon}
  </button>
);

/* Self-contained "⋮" menu for simple cards (Edit / Remove) */
const CardMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, useCallback(() => setOpen(false), []));
  return (
    <div className={styles.actionGroup} ref={ref}>
      <IconActionBtn icon={<MoreVertical size={16} />} title="More actions" onClick={() => setOpen(!open)} />
      {open && (
        <div className={styles.actionDropdown}>
          <div className={styles.dropdownSection}>
            {items.map((it, i) => (
              <button key={i} onClick={() => { it.onClick(); setOpen(false); }} className={it.danger ? styles.dropdownDanger : undefined}>
                {it.icon} {it.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* 3-column summary bar: Product Summary | Supplier | Supplier Ref */
const SummaryBar = ({ title, subtitle, supplier, supplierRef, showToast }) => (
  <div className={styles.uSummary}>
    <div className={styles.uSumCell}>
      <span className={styles.uSumLabel}>Product Summary</span>
      <span className={styles.uSumTitle}>{title || "—"}</span>
      {subtitle ? <span className={styles.uSumSub}>{subtitle}</span> : null}
    </div>
    <div className={styles.uSumCell}>
      <span className={styles.uSumLabel}>Supplier</span>
      <span className={styles.uSumTitle}>{supplier || "—"}</span>
    </div>
    <div className={styles.uSumCell}>
      <span className={styles.uSumLabel}>Supplier Ref</span>
      <span className={styles.uSumTitle}><CopyableValue label="Supplier Ref" value={supplierRef} showToast={showToast} /></span>
    </div>
  </div>
);

/* Icon-stat strip: row of [circle icon] LABEL / value */
const StatStrip = ({ items }) => (
  <div className={styles.uStats}>
    {items.filter(Boolean).map((s, i) => (
      <div key={i} className={styles.uStat}>
        <span className={styles.uStatIcon}>{s.icon}</span>
        <span className={styles.uStatTxt}>
          <span className={styles.uStatLabel}>{s.label}</span>
          <span className={styles.uStatValue} title={typeof s.value === "string" ? s.value : undefined}>{s.value || "—"}</span>
        </span>
      </div>
    ))}
  </div>
);

/* Footer: chips (left) + View Details button (right) */
const FooterBar = ({ chips = [], onView }) => (
  <div className={styles.uFooter}>
    <div className={styles.uChips}>
      {chips.filter((c) => c && c.value && c.value !== "—").map((c, i) => (
        <div key={i} className={styles.uChip}>
          <span className={styles.uChipLabel}>{c.label}</span>
          <span className={styles.uChipValue}>{c.value}</span>
        </div>
      ))}
    </div>
    {onView && (
      <button type="button" className={styles.uViewBtn} onClick={onView}>
        View Details <ChevronRight size={15} />
      </button>
    )}
  </div>
);

/* Map a stat field label → a sensible neutral icon */
const STAT_ICON_FOR_LABEL = (label = "") => {
  const l = label.toLowerCase();
  if (l.includes("traveler") || l.includes("driver") || l.includes("passenger") || l.includes("holder")) return <Users size={16} />;
  if (l.includes("date") || l.includes("check-in") || l.includes("check-out") || l.includes("departure") || l.includes("arrival") || l.includes("valid")) return <Calendar size={16} />;
  if (l.includes("time") || l.includes("processing") || l.includes("night")) return <Clock size={16} />;
  if (l.includes("country") || l.includes("destination") || l.includes("coverage") || l.includes("area")) return <Globe size={16} />;
  if (l.includes("location") || l.includes("pickup") || l.includes("dropoff") || l.includes("pick-up") || l.includes("drop-off") || l.includes("address")) return <MapPin size={16} />;
  if (l.includes("vehicle") || l.includes("car")) return <Car size={16} />;
  if (l.includes("room") || l.includes("board")) return <Bed size={16} />;
  if (l.includes("vat")) return <Percent size={16} />;
  if (l.includes("ref") || l.includes("id") || l.includes("number") || l.includes("status")) return <Tag size={16} />;
  return <Tag size={16} />;
};

/* ══════════════════════════════════════════════
   CANCELLATION ENGINE — itinerary card additions
   Standardised across every product type:
     • status        Active / Partially Cancelled / Cancelled
     • travelers     total / active / cancelled
     • breakdown     which part of the product is cancelled
     • summary       cancellation case, refund, fee, credit-note status
     • notice        standardised cancellation message
   Neutral palette — only the status TEXT is coloured. (client spec)
   ══════════════════════════════════════════════ */
const fmtCancDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return ""; }
};
const fmtCancMoney = (v) => {
  if (v == null || v === "" || Number.isNaN(parseFloat(v))) return "—";
  return `€ ${parseFloat(v).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/* Build the cancellation view-model for a single product, or null when the
   product is fully active. Sourced from the product's own cancellation block
   PLUS the matching cancellation case (authoritative for fee / refund / CN). */
const buildCardCancellation = ({ component, cancellationCases = [], travelers = [], assignments = [] }) => {
  const num = (v) => parseFloat(v || 0);
  const raw = component._raw || {};
  const d = raw.productDetails || {};
  const cn = d.cancellation || {};
  const isCancelled = component.status === "Cancelled";
  const cancelledDirections = cn.cancelledDirections || [];
  const directionFees = cn.directionFees || [];
  const legs = d.legs || [];
  const rooms = (Array.isArray(d.rooms) ? d.rooms : []);
  const anyLegCancelled = legs.some((l) => l.legStatus === "Cancelled" || l.flightStatus === "Cancelled");
  const anyRoomCancelled = rooms.some((r) => (r.status || r.roomStatus) === "Cancelled");
  const hasPartial = !isCancelled && (directionFees.length > 0 || cancelledDirections.length > 0 || anyLegCancelled || anyRoomCancelled);
  if (!isCancelled && !hasPartial) return null;
  const state = isCancelled ? "Cancelled" : "Partial";

  // Matching cancellation case (one of its lines references this product id).
  const cases = Array.isArray(cancellationCases) ? cancellationCases : [];
  const pcase = cases
    .filter((c) => Array.isArray(c.lines) && c.lines.some((l) => String(l.productId) === String(component.id)))
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0] || null;
  const line = pcase ? (pcase.lines.find((l) => String(l.productId) === String(component.id)) || null) : null;

  // Financials — prefer the confirmed case line, else fall back to the product block.
  let supplierFee, agencyFee, refundDue;
  if (line) {
    supplierFee = num(line.cancCost); agencyFee = num(line.cancFee); refundDue = num(line.customerRefund);
  } else {
    if (isCancelled) { supplierFee = num(cn.cost); agencyFee = num(cn.fee); }
    else {
      supplierFee = directionFees.reduce((s, x) => s + num(x.cost), 0);
      agencyFee = directionFees.reduce((s, x) => s + num(x.fee), 0);
    }
    const r = directionFees.reduce((s, x) => s + num(x.refund), 0);
    refundDue = r > 0 ? r : null; // unknown for a full cancel without a case
  }
  const cancellationFee = supplierFee + agencyFee;

  // Credit note (per matching case).
  let creditNote = null;
  if (pcase) {
    if (pcase.status === "Reversed") creditNote = { number: pcase.documentNumber || null, status: "Cancelled", issuedOn: fmtCancDate(pcase.createdAt) };
    else if (pcase.documentNumber) creditNote = { number: pcase.documentNumber, status: "Issued", issuedOn: fmtCancDate(pcase.createdAt) };
    else if (pcase.documentDecision === "credit") creditNote = { number: null, status: "Pending", issuedOn: "" };
  }

  // Case meta.
  const caseNumber = pcase?.caseNumber || cn.caseNumber || null;
  const cancelledOn = fmtCancDate(pcase?.createdAt || cn.recordedAt);
  const cancelledBy = pcase?.createdByName || pcase?.creator?.name || (pcase?.createdBy != null ? `User #${pcase.createdBy}` : "—");
  const cancelType = isCancelled ? "Full Cancellation" : "Partial Cancellation";

  // Traveler summary (assignment-scoped). A fully cancelled product cancels all of them.
  const assignedIds = [...new Set((assignments || []).filter((a) => String(a.productId) === String(component.id)).map((a) => a.travelerId))];
  const totalTravelers = assignedIds.length || component.travelersCount || 0;
  const cancByStatus = (travelers || []).filter((t) => t.status === "Cancelled" && assignedIds.includes(t.id)).length;
  const cancelledTravelers = isCancelled ? totalTravelers : cancByStatus;
  const activeTravelers = Math.max(totalTravelers - cancelledTravelers, 0);

  // Product breakdown — which part is cancelled (rooms / transfer legs).
  let parts = [];
  let partsTitle = "";
  if (component.type === "Hotel") {
    partsTitle = "Rooms Breakdown";
    const list = rooms.length ? rooms : [{}];
    parts = list.map((r, i) => ({
      label: r.roomName || r.roomType || `Room ${i + 1}`,
      sub: [r.boardName || r.boardCode, r.roomCharacteristic].filter(Boolean).join(" · "),
      status: (isCancelled || (r.status || r.roomStatus) === "Cancelled") ? "Cancelled" : "Active",
    }));
  } else if (component.type === "Transfer") {
    partsTitle = "Transfer Breakdown";
    if (d.transferMoment === "RoundTrip") {
      const legStatus = (leg) => (isCancelled || leg?.status === "Cancelled") ? "Cancelled" : "Active";
      parts = [
        { label: "Arrival Transfer", sub: d.arrivalLeg?.schedule?.pickupLocation || "", status: legStatus(d.arrivalLeg) },
        { label: "Departure Transfer", sub: d.departureLeg?.schedule?.pickupLocation || "", status: legStatus(d.departureLeg) },
      ];
    } else {
      parts = [{ label: d.transferMoment ? `${d.transferMoment} Transfer` : "Transfer", sub: d.transferType || "", status: isCancelled ? "Cancelled" : "Active" }];
    }
  }

  return {
    state, caseNumber, cancelledOn, cancelledBy, cancelType,
    supplierFee, agencyFee, cancellationFee, refundDue, creditNote,
    totalTravelers, activeTravelers, cancelledTravelers,
    cancelledDirections, parts, partsTitle,
  };
};

/* Header chip — "Cancellation Case: CAN-…" with copy-to-clipboard. */
const CancCaseRef = ({ canc, showToast }) => {
  if (!canc || !canc.caseNumber) return null;
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(canc.caseNumber)
      .then(() => showToast?.("Cancellation case copied", "success"))
      .catch(() => showToast?.("Failed to copy case number", "error"));
  };
  return (
    <span className={styles.uCaseRef}>
      <span className={styles.uCaseRefLabel}>Cancellation Case:</span>
      <span className={styles.uCaseRefNum}>{canc.caseNumber}</span>
      <button type="button" className={styles.uCaseRefCopy} onClick={copy} title="Copy case number"><Copy size={11} /></button>
    </span>
  );
};

/* Travelers stat value — standardised total / active / cancelled split. */
const travelerStatValue = (canc, tc) => {
  if (!canc) return `${tc} traveler${tc !== 1 ? "s" : ""}`;
  return (
    <span className={styles.uTravSplit}>
      <span className={styles.uTravTotal}>{canc.totalTravelers} total</span>
      <span className={styles.uTravSub}>
        {canc.activeTravelers > 0 && <span className={styles.uTravActive}>{canc.activeTravelers} Active</span>}
        {canc.activeTravelers > 0 && canc.cancelledTravelers > 0 && <span className={styles.uTravMid}>·</span>}
        {canc.cancelledTravelers > 0 && <span className={styles.uTravCancelled}>{canc.cancelledTravelers} Cancelled</span>}
      </span>
    </span>
  );
};

/* Right-side Cancellation Summary panel. */
const CN_PILL_CLASS = { Issued: "uCnIssued", Pending: "uCnPending", Draft: "uCnDraft", Cancelled: "uCnCancelledPill" };
const CancAside = ({ canc }) => {
  if (!canc) return null;
  const cn = canc.creditNote;
  return (
    <aside className={styles.uCancAside}>
      <div className={styles.uCancHead}>Cancellation Summary</div>
      <div className={styles.uCancRows}>
        <div className={styles.uCancRow}><span className={styles.uCancLabel}>Cancelled On</span><span className={styles.uCancVal}>{canc.cancelledOn || "—"}</span></div>
        <div className={styles.uCancRow}><span className={styles.uCancLabel}>Cancelled By</span><span className={styles.uCancVal}>{canc.cancelledBy || "—"}</span></div>
        <div className={styles.uCancRow}><span className={styles.uCancLabel}>Cancel Type</span><span className={styles.uCancVal}>{canc.cancelType}</span></div>
        <div className={styles.uCancDivider} />
        <div className={styles.uCancRow}><span className={styles.uCancLabel}>Refund Due</span><span className={`${styles.uCancVal} ${styles.uCancRefund}`}>{fmtCancMoney(canc.refundDue)}</span></div>
        <div className={styles.uCancRow}><span className={styles.uCancLabel}>Cancellation Fee</span><span className={`${styles.uCancVal} ${styles.uCancFee}`}>{fmtCancMoney(canc.cancellationFee)}</span></div>
        <div className={styles.uCancRow}>
          <span className={styles.uCancLabel}>Credit Note</span>
          <span className={styles.uCancCnWrap}>
            <span className={styles.uCancCnNum}>{cn?.number || "—"}</span>
            {cn?.status && <span className={`${styles.uCnPill} ${styles[CN_PILL_CLASS[cn.status]] || ""}`}>{cn.status}</span>}
          </span>
        </div>
      </div>
    </aside>
  );
};

/* Wrapper: SummaryBar + StatStrip on the left, Cancellation Summary on the right.
   When the product is fully active it renders its children unchanged. */
const CancTop = ({ canc, children }) =>
  canc ? (
    <div className={styles.uTopSplit}>
      <div className={styles.uTopSplitMain}>{children}</div>
      <CancAside canc={canc} />
    </div>
  ) : (
    <>{children}</>
  );

/* Standardised product breakdown (rooms / transfer legs / …). */
const PartsBreakdown = ({ title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className={styles.uPartsBox}>
      <div className={styles.uPartsHead}>{title}</div>
      <div className={styles.uPartsList}>
        {items.map((it, i) => (
          <div key={i} className={styles.uPartsRow}>
            <span className={styles.uPartsName}>
              {it.label}
              {it.sub ? <span className={styles.uPartsSub}>{it.sub}</span> : null}
            </span>
            <span className={`${styles.uPartsStatus} ${it.status === "Cancelled" ? styles.uPartsCancelled : styles.uPartsActive}`}>
              <span className={styles.uPartsDot} />{it.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* Standardised cancellation notice. */
const CancNotice = ({ canc }) => {
  if (!canc) return null;
  const msg = canc.state === "Cancelled"
    ? "This product has been fully cancelled. Refund and credit note will be processed."
    : "Only the cancelled portion has been cancelled. Refund and credit note will be processed for the cancelled portion.";
  return (
    <div className={styles.uCancNotice}>
      <AlertTriangle size={15} className={styles.uCancNoticeIcon} />
      <span>{msg}</span>
    </div>
  );
};

/* ══════════════════════════════════════════════
   REMOVE CONFIRMATION MODAL
   ══════════════════════════════════════════════ */
const REMOVE_RULES = {
  Flight: [
    "No supplier payments must exist for this flight.",
    "No travelers may be assigned to this flight.",
    "No pricing records may exist for this flight.",
    "No post-booking actions may exist for this flight.",
    "No documents may be linked to this flight.",
    "The flight must not be linked to any active package logic or booking dependencies.",
    "The flight must not have any active schedule change workflows.",
  ],
  Hotel: [
    "No supplier payments must exist for this accommodation.",
    "No travelers may be assigned to this accommodation.",
    "No room pricing records may exist.",
    "No post-booking actions may exist for this accommodation.",
    "No documents may be linked to this accommodation.",
    "The accommodation must not be linked to any active package logic or itinerary dependencies.",
    "The accommodation must not have active check-in/check-out operational workflows.",
  ],
  Transfer: [
    "No supplier payments must exist for this transfer.",
    "No travelers may be assigned to this transfer.",
    "No pricing records may exist for this transfer.",
    "No operational driver assignment may exist.",
    "No post-booking actions may exist for this transfer.",
    "No documents may be linked to this transfer.",
    "The transfer must not be linked to active flight synchronization or operational workflows.",
    "The transfer may not be removed if it is linked to an active travel itinerary or package booking.",
  ],
};

const RemoveModal = ({ open, onClose, component, onConfirm }) => {
  if (!open || !component) return null;
  const rules = REMOVE_RULES[component.type] || [];
  const typeName = component.type === "Hotel" ? "Accommodation" : component.type;

  return (
    <BaseModal open={open} onClose={onClose} title={`Remove ${typeName}`} width="540px">
      <div className={styles.modalBody}>
        <div className={styles.warningBanner}>
          <AlertTriangle size={18} />
          <span>Are you sure you want to remove this {typeName.toLowerCase()}?</span>
        </div>
        {rules.length > 0 && (
          <div className={styles.validationSection}>
            <div className={styles.validationTitle}>Validation Rules</div>
            <div className={styles.validationSubtitle}>
              This {typeName.toLowerCase()} may only be removed if all rules below are satisfied:
            </div>
            <ol className={styles.validationList}>
              {rules.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ol>
          </div>
        )}
        <div className={styles.modalActions}>
          <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button className={styles.btnDanger} onClick={() => onConfirm(component)}>
            <Trash2 size={13} /> Remove {typeName}
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

/* ══════════════════════════════════════════════
   COPY FLIGHT MODAL
   Smart PNR: detects unique airlines from legs,
   shows 1 PNR field per airline
   ══════════════════════════════════════════════ */
const CopyFlightModal = ({ open, onClose, component, onConfirm }) => {
  const d = component?._raw?.productDetails || {};
  const legs = d.legs || [];

  // Extract unique airlines from legs
  const airlines = useMemo(() => {
    const map = new Map();
    legs.forEach((leg) => {
      const code = (leg.airlineCode || "").toUpperCase();
      if (code && !map.has(code)) {
        map.set(code, {
          code,
          name: leg.airlineName || code,
          // Collect route info for context
          routes: [],
        });
      }
      if (code && map.has(code)) {
        map.get(code).routes.push(`${(leg.departure || leg.departureAirport || "").toUpperCase()}→${(leg.arrival || leg.arrivalAirport || "").toUpperCase()}`);
      }
    });
    return Array.from(map.values());
  }, [legs]);

  const isSingleAirline = airlines.length <= 1;

  const [supplierRef, setSupplierRef] = useState("");
  const [pnrs, setPnrs] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSupplierRef("");
      setPnrs({});
    }
  }, [open]);

  const updatePnr = (airlineCode, value) => {
    setPnrs((prev) => ({ ...prev, [airlineCode]: value }));
  };

  const handleSave = () => {
    if (!supplierRef) return;
    onConfirm({ supplierRef, pnrs, isSingleAirline });
    onClose();
  };

  if (!open) return null;

  return (
    <BaseModal open={open} onClose={onClose} title="Copy Flight" width="460px">
      <div className={styles.modalBody}>
        <div className={styles.formGroup}>
          <label>Supplier Reference Number <span className={styles.required}>*</span></label>
          <input
            type="text"
            value={supplierRef}
            onChange={(e) => setSupplierRef(e.target.value)}
            placeholder="Enter new supplier reference"
          />
        </div>

        {isSingleAirline ? (
          /* Single airline — one PNR field */
          <div className={styles.formGroup}>
            <label>Airline PNR {airlines[0] ? <span className={styles.airlineTag}>{airlines[0].code}</span> : null}</label>
            <input
              type="text"
              value={pnrs[airlines[0]?.code || "_single"] || ""}
              onChange={(e) => updatePnr(airlines[0]?.code || "_single", e.target.value)}
              placeholder="Enter PNR (optional — can be added later)"
            />
          </div>
        ) : (
          /* Multiple airlines — PNR per airline */
          <>
            <div className={styles.copyPnrSection}>
              <div className={styles.copyPnrTitle}>
                <Plane size={13} /> {airlines.length} airlines detected — enter PNR per airline
              </div>
              {airlines.map((airline) => (
                <div key={airline.code} className={styles.formGroup}>
                  <label>
                    <span className={styles.airlineTag}>{airline.code}</span> PNR
                    <span className={styles.airlineRoutes}>{airline.routes.join(", ")}</span>
                  </label>
                  <input
                    type="text"
                    value={pnrs[airline.code] || ""}
                    onChange={(e) => updatePnr(airline.code, e.target.value)}
                    placeholder={`PNR for ${airline.name} (optional)`}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        <div className={styles.modalActions}>
          <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={!supplierRef}>
            <Copy size={13} /> Copy Flight
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

/* ══════════════════════════════════════════════
   UPDATE FLIGHT SCHEDULE MODAL
   Per-leg editing with current vs new side-by-side
   ══════════════════════════════════════════════ */
const UpdateScheduleModal = ({ open, onClose, component, onConfirm, airportMap = {}, legIndex = 0 }) => {
  const raw = component?._raw || {};
  const d = raw.productDetails || {};
  const legs = d.legs || [];

  const buildLegForm = (leg) => ({
    departureAirport: leg.departure || leg.departureAirport || "",
    arrivalAirport: leg.arrival || leg.arrivalAirport || "",
    departureDate: leg.date || leg.departureDate || "",
    departureTime: leg.time || leg.departureTime || "",
    arrivalDate: leg.arrivalDate || leg.date || leg.departureDate || "",
    arrivalTime: leg.arrivalTime || "",
    flightCode: `${leg.airlineCode || ""}${leg.flightNumber || ""}`,
  });

  const [activeLeg, setActiveLeg] = useState(legIndex);
  const [legForms, setLegForms] = useState([]);
  const [changeType, setChangeType] = useState("Schedule Change");
  const [changeImpact, setChangeImpact] = useState("Minor");
  const [detectedVia, setDetectedVia] = useState("Manual");
  const [notes, setNotes] = useState("");
  const [customerNotified, setCustomerNotified] = useState(false);
  const [docsRegenerated, setDocsRegenerated] = useState(false);

  useEffect(() => {
    if (open) {
      setLegForms(legs.map(buildLegForm));
      setActiveLeg(legIndex);
      setChangeType("Schedule Change");
      setChangeImpact("Minor");
      setDetectedVia("Manual");
      setNotes("");
      setCustomerNotified(false);
      setDocsRegenerated(false);
    }
  }, [open]);

  const currentLeg = legs[activeLeg] || {};
  const currentForm = legForms[activeLeg] || {};
  const depCode = (currentLeg.departure || currentLeg.departureAirport || "").toUpperCase();
  const arrCode = (currentLeg.arrival || currentLeg.arrivalAirport || "").toUpperCase();
  const curDepName = airportMap[depCode] || depCode;
  const curArrName = airportMap[arrCode] || arrCode;
  const curDepTime = currentLeg.time || currentLeg.departureTime || "—";
  const curArrTime = currentLeg.arrivalTime || "—";
  const curDate = currentLeg.date || currentLeg.departureDate || "";
  const curFlight = `${currentLeg.airlineCode || ""}${currentLeg.flightNumber || ""}`;

  const updateLegField = (field, value) => {
    setLegForms((prev) => prev.map((f, i) => i === activeLeg ? { ...f, [field]: value } : f));
  };

  const updateLegFields = (updates) => {
    setLegForms((prev) => prev.map((f, i) => i === activeLeg ? { ...f, ...updates } : f));
  };

  const hasChanges = legForms.some((f, i) => {
    const leg = legs[i] || {};
    const orig = buildLegForm(leg);
    return Object.keys(orig).some((k) => f[k] !== orig[k]);
  });

  const handleSave = () => {
    onConfirm({ legForms, changeType, changeImpact, detectedVia, notes, customerNotified, docsRegenerated });
    onClose();
  };

  if (!open || !component) return null;

  return (
    <BaseModal open={open} onClose={onClose} title="Update Flight Schedule" width="780px">
      <div className={styles.modalBody}>
        {/* Leg Selector Tabs (for multi-leg flights) */}
        {legs.length > 1 && (
          <div className={styles.schLegTabs}>
            {legs.map((leg, i) => {
              const dep = (leg.departure || leg.departureAirport || "").toUpperCase();
              const arr = (leg.arrival || leg.arrivalAirport || "").toUpperCase();
              return (
                <button
                  key={i}
                  className={`${styles.schLegTab} ${i === activeLeg ? styles.schLegTabActive : ""}`}
                  onClick={() => setActiveLeg(i)}
                >
                  <span className={styles.schLegTabDir}>{leg.direction || `Segment ${i + 1}`}</span>
                  <span className={styles.schLegTabRoute}>{dep} → {arr}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Current vs New — Inline comparison */}
        <div className={styles.schCompareSection}>
          {/* Current (read-only) */}
          <div className={styles.schCurrentBlock}>
            <div className={styles.schBlockLabel}><Clock size={12} /> Current Schedule</div>
            <div className={styles.schCurrentRoute}>
              <div className={styles.schCurrentEndpoint}>
                <span className={styles.schCode}>{depCode}</span>
                <span className={styles.schName}>{curDepName}</span>
              </div>
              <ArrowRight size={14} className={styles.routeArrow} />
              <div className={styles.schCurrentEndpoint}>
                <span className={styles.schCode}>{arrCode}</span>
                <span className={styles.schName}>{curArrName}</span>
              </div>
            </div>
            <div className={styles.schCurrentMeta}>
              <span>{formatDate(curDate)}</span>
              <span>{curDepTime} → {curArrTime}</span>
              <span className={styles.schFlightBadge}>{curFlight || "—"}</span>
            </div>
          </div>

          {/* New (editable) */}
          <div className={styles.schNewBlock}>
            <div className={styles.schBlockLabel}><Pencil size={12} /> New Schedule</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>From</label>
                <input type="text" value={currentForm.departureAirport} onChange={(e) => updateLegField("departureAirport", e.target.value)} placeholder="IATA code" />
              </div>
              <div className={styles.formGroup}>
                <label>To</label>
                <input type="text" value={currentForm.arrivalAirport} onChange={(e) => updateLegField("arrivalAirport", e.target.value)} placeholder="IATA code" />
              </div>
              <div className={styles.formGroup}>
                <label>Flight Code</label>
                <input type="text" value={currentForm.flightCode} onChange={(e) => updateLegField("flightCode", e.target.value)} placeholder="e.g. XQ903" />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Departure Date</label>
                <input type="date" value={currentForm.departureDate} onChange={(e) => {
                  const depDate = e.target.value;
                  let arrDate = depDate;
                  if (depDate && currentForm.arrivalTime && currentForm.departureTime && currentForm.arrivalTime < currentForm.departureTime) {
                    const next = new Date(depDate);
                    next.setDate(next.getDate() + 1);
                    arrDate = next.toISOString().slice(0, 10);
                  }
                  updateLegFields({ departureDate: depDate, arrivalDate: arrDate });
                }} />
              </div>
              <div className={styles.formGroup}>
                <label>Departure Time</label>
                <input type="time" value={currentForm.departureTime} onChange={(e) => {
                  const depTime = e.target.value;
                  const updates = { departureTime: depTime };
                  if (currentForm.departureDate && currentForm.arrivalTime && depTime) {
                    if (currentForm.arrivalTime < depTime) {
                      const next = new Date(currentForm.departureDate);
                      next.setDate(next.getDate() + 1);
                      updates.arrivalDate = next.toISOString().slice(0, 10);
                    } else {
                      updates.arrivalDate = currentForm.departureDate;
                    }
                  }
                  updateLegFields(updates);
                }} />
              </div>
              <div className={styles.formGroup}>
                <label>Arrival Date</label>
                <input type="date" value={currentForm.arrivalDate} onChange={(e) => updateLegField("arrivalDate", e.target.value)} />
              </div>
              <div className={styles.formGroup}>
                <label>Arrival Time</label>
                <input type="time" value={currentForm.arrivalTime} onChange={(e) => {
                  const arrTime = e.target.value;
                  const updates = { arrivalTime: arrTime };
                  if (currentForm.departureDate && currentForm.departureTime && arrTime) {
                    if (arrTime < currentForm.departureTime) {
                      const next = new Date(currentForm.departureDate);
                      next.setDate(next.getDate() + 1);
                      updates.arrivalDate = next.toISOString().slice(0, 10);
                    } else {
                      updates.arrivalDate = currentForm.departureDate;
                    }
                  }
                  updateLegFields(updates);
                }} />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.separator} />

        {/* Change Classification */}
        <div className={styles.schClassRow}>
          <div className={styles.formGroup}>
            <label>Change Type</label>
            <select value={changeType} onChange={(e) => setChangeType(e.target.value)}>
              <option value="Schedule Change">Schedule Change</option>
              <option value="Flight Rescheduled">Flight Rescheduled</option>
              <option value="Airport Change">Airport Change</option>
              <option value="Flight Cancellation">Flight Cancellation</option>
              <option value="Route Change">Route Change</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Impact</label>
            <select value={changeImpact} onChange={(e) => setChangeImpact(e.target.value)}>
              <option value="Minor">Minor</option>
              <option value="Major">Major</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Detected Via</label>
            <select value={detectedVia} onChange={(e) => setDetectedVia(e.target.value)}>
              <option value="Manual">Manual</option>
              <option value="API">Supplier API</option>
              <option value="Email">Email</option>
              <option value="GDS">GDS</option>
            </select>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>Notes <span className={styles.optional}>(optional)</span></label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes about this schedule change..." />
        </div>

        {/* Notification toggles */}
        <div className={styles.docComSection}>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" checked={customerNotified} onChange={(e) => setCustomerNotified(e.target.checked)} />
            Customer has been notified
          </label>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" checked={docsRegenerated} onChange={(e) => setDocsRegenerated(e.target.checked)} />
            Regenerate travel documents
          </label>
        </div>

        <div className={styles.modalActions}>
          <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={!hasChanges}>
            <Check size={13} /> Save Schedule Change
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

/* ══════════════════════════════════════════════
   POST-BOOKING MODIFICATION MODAL (smart per action)
   ══════════════════════════════════════════════ */
const ACTION_TYPE_TO_ENUM = {
  "Date Change": "DATE_CHANGE",
  "Route Change": "ROUTE_CHANGE",
  "Flight Change / Rebooking": "SCHEDULE_CHANGE",
  "No-Show Handling": "CANCELLATION_FEE",
  "Refund Request": "OTHER",
  "Airline Cost Recovery": "OTHER",
  "Date Modification": "DATE_CHANGE",
  "Room Change": "ROOM_CHANGE",
  "Board Type Change": "UPGRADE",
  "Add Extra Night": "SCHEDULE_CHANGE",
  "Transfer Rebook": "SCHEDULE_CHANGE",
  "Traveler Count Change": "VEHICLE_CHANGE",
  "Other Modification": "OTHER",
};

const ACTION_CONFIG = {
  "Date Change": {
    icon: Clock, color: "#3b82f6",
    description: "Change the travel date, times, airline or flight code for this flight",
    fields: ["currentDate", "newDate", "newTime", "newFlightCode"],
  },
  "Route Change": {
    icon: MapPin, color: "#8b5cf6",
    description: "Change the route / airports, times, airline or flight code for this flight",
    fields: ["currentRoute", "newRoute", "newTime", "newFlightCode"],
  },
  "Flight Change / Rebooking": {
    icon: RefreshCw, color: "#f59e0b",
    description: "Rebook to a different flight — date, times, route, and flight code",
    fields: ["currentDate", "newDate", "currentRoute", "newRoute", "newFlightCode", "newTime"],
  },
  "No-Show Handling": {
    icon: AlertTriangle, color: "#ef4444",
    description: "Record a no-show and handle financial impact",
    fields: ["noShowDate"],
  },
  "Refund Request": {
    icon: RotateCcw, color: "#10b981",
    description: "Submit a refund request to the supplier",
    fields: ["refundReason"],
    financialFocus: "refund",
  },
  "Airline Cost Recovery": {
    icon: DollarSign, color: "#f59e0b",
    description: "Recover costs from the airline for disruption",
    fields: ["recoveryReason"],
    financialFocus: "recovery",
  },
  "Date Modification": {
    icon: Clock, color: "#3b82f6",
    description: "Change check-in/check-out dates for this accommodation",
    fields: ["hotelDates"],
  },
  "Room Change": {
    icon: ArrowRight, color: "#8b5cf6",
    description: "Change room type, characteristics, board or other room details",
    fields: ["roomDetails"],
  },
  "Board Type Change": {
    icon: ArrowRight, color: "#8b5cf6",
    description: "Change the board / meal plan",
    fields: ["currentBoard", "newBoard"],
  },
  "Add Extra Night": {
    icon: Plus, color: "#10b981",
    description: "Extend the stay with additional night(s)",
    fields: ["extraNights"],
  },
  "Transfer Rebook": {
    icon: RefreshCw, color: "#f59e0b",
    description: "Rebook transfer to a different date, time or route",
    fields: ["currentDate", "newDate"],
  },
  "Traveler Count Change": {
    icon: Users, color: "#3b82f6",
    description: "Change the number of travelers on this transfer",
    fields: ["currentCount", "newCount"],
  },
  "Other Modification": {
    icon: Pencil, color: "#6b7280",
    description: "Record any other post-booking modification",
    fields: [],
  },
};

/* ── Flight actions that need leg selection ── */
const FLIGHT_POST_ACTIONS = [
  "Date Change", "Route Change", "Flight Change / Rebooking",
  "No-Show Handling", "Refund Request", "Airline Cost Recovery",
];

const PostBookingModal = ({ open, onClose, component, actionType, onConfirm, airportMap = {} }) => {
  const config = ACTION_CONFIG[actionType] || ACTION_CONFIG["Other Modification"];
  const ActionIcon = config.icon || Pencil;
  const raw = component?._raw || {};
  const d = raw.productDetails || {};
  const legs = d.legs || [];
  const firstLeg = legs[0] || {};
  const flightType = d.flightType || "OW";

  /* ── Flight/Leg selection state ── */
  const isFlightAction = component?.type === "Flight" && FLIGHT_POST_ACTIONS.includes(actionType);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [selectedLegIndices, setSelectedLegIndices] = useState([]);

  /* Group legs by direction */
  const legGroups = useMemo(() => {
    if (!isFlightAction || legs.length === 0) return {};
    const groups = {};
    legs.forEach((leg, idx) => {
      let dir = leg.direction || "";
      if (!dir) {
        if (flightType === "RT") dir = idx < Math.ceil(legs.length / 2) ? "Outbound" : "Inbound";
        else dir = "Outbound";
      }
      if (!groups[dir]) groups[dir] = [];
      groups[dir].push({ leg, index: idx });
    });
    return groups;
  }, [legs, isFlightAction, flightType]);

  /* Direction button options */
  const directionOptions = useMemo(() => {
    const dirs = Object.keys(legGroups);
    if (dirs.length >= 2) return [...dirs, "Both"];
    return dirs;
  }, [legGroups]);

  /* Auto-select legs when direction changes */
  useEffect(() => {
    if (!selectedDirection) { setSelectedLegIndices([]); return; }
    if (selectedDirection === "Both") {
      setSelectedLegIndices(legs.map((_, i) => i));
    } else {
      const group = legGroups[selectedDirection] || [];
      setSelectedLegIndices(group.map((g) => g.index));
    }
  }, [selectedDirection]);

  /* Legs to display in the selector */
  const displayLegs = useMemo(() => {
    if (!selectedDirection) return [];
    if (selectedDirection === "Both") return legs.map((leg, idx) => ({ leg, index: idx }));
    return legGroups[selectedDirection] || [];
  }, [selectedDirection, legGroups, legs]);

  /* For single-leg flights, auto-select without showing the selector */
  const skipLegSelector = isFlightAction && legs.length <= 1;

  const [airportOptions, setAirportOptions] = useState([]);
  const handleAirportSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setAirportOptions([]); return; }
    try {
      const res = await getAirports({ search: query, limit: 20, sortBy: "code", sortOrder: "ASC" });
      const list = res?.data || res || [];
      setAirportOptions(list.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}` })));
    } catch { setAirportOptions([]); }
  }, []);

  const [airlineOptions, setAirlineOptions] = useState([]);
  const handleAirlineSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setAirlineOptions([]); return; }
    try {
      const res = await getAirlines({ search: query, limit: 20, status: "Active" });
      const list = res?.data || res || [];
      setAirlineOptions(list.map((a) => ({
        value: (a.iataCode || "").toUpperCase(),
        name: a.name || "",
        label: `${(a.iataCode || "").toUpperCase()} — ${a.name || ""}`,
      })));
    } catch { setAirlineOptions([]); }
  }, []);

  const buildLegEdits = () =>
    legs.map((leg) => ({
      currentFrom: (leg.departure || leg.departureAirport || "").toUpperCase(),
      currentTo: (leg.arrival || leg.arrivalAirport || "").toUpperCase(),
      currentDate: leg.date || leg.departureDate || "",
      currentFlightCode: leg.flightNumber || leg.flightCode || "",
      currentAirlineCode: (leg.airlineCode || "").toUpperCase(),
      currentAirlineName: leg.airlineName || "",
      currentDepartureTime: (leg.time || leg.departureTime || "").slice(0, 5),
      currentArrivalTime: (leg.arrivalTime || "").slice(0, 5),
      direction: leg.direction || (legs.indexOf(leg) === 0 ? "Outbound" : "Inbound"),
      newFrom: "",
      newTo: "",
      newDate: "",
      // Airline & flight code pre-filled with the current values so the user only
      // tweaks what changed; the dropdown shows the current airline via initialLabel.
      newFlightCode: leg.flightNumber || leg.flightCode || "",
      newAirlineCode: (leg.airlineCode || "").toUpperCase(),
      newAirlineName: leg.airlineName || "",
      newDepartureTime: "",
      newArrivalTime: "",
    }));

  const rooms = (Array.isArray(d.rooms) ? d.rooms : []);
  const firstRoom = rooms[0] || {};

  const [form, setForm] = useState({
    modificationType: ACTION_TYPE_TO_ENUM[actionType] || "OTHER",
    modificationTitle: "",
    internalNotes: "",
    legEdits: buildLegEdits(),
    // Hotel dates
    currentCheckIn: "",
    currentCheckOut: "",
    currentNights: "",
    newCheckIn: "",
    newCheckOut: "",
    newNights: "",
    // Hotel room details
    currentRoom: "",
    newRoom: "",
    currentBoard: "",
    newBoard: "",
    newRoomType: "",
    newRoomCharacteristic: "",
    newRoomStatus: "",
    newRoomRate: "",
    newNumberOfRooms: "",
    newBoardCode: "",
    newBoardName: "",
    extraNights: "1",
    // Transfer-specific
    currentCount: "",
    newCount: "",
    // No-show
    noShowDate: "",
    // Refund / Recovery
    refundReason: "",
    recoveryReason: "",
    // Financial
    supplierCost: "",
    agencyFee: "",
    refundAmount: "",
    regenerateDocuments: true,
    notifyCustomer: true,
  });

  const recalcNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return "";
    const diff = Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000);
    return diff > 0 ? String(diff) : "";
  };

  useEffect(() => {
    if (open) {
      /* Reset flight/leg selection */
      if (skipLegSelector) {
        setSelectedDirection("Both");
        setSelectedLegIndices(legs.length === 1 ? [0] : []);
      } else {
        setSelectedDirection(null);
        setSelectedLegIndices([]);
      }

      setForm((prev) => ({
        ...prev,
        modificationType: ACTION_TYPE_TO_ENUM[actionType] || "OTHER",
        modificationTitle: "",
        internalNotes: "",
        supplierCost: "",
        agencyFee: "",
        refundAmount: "",
        regenerateDocuments: true,
        notifyCustomer: true,
        legEdits: buildLegEdits(),
        // Hotel dates
        currentCheckIn: d.checkIn || "",
        currentCheckOut: d.checkOut || "",
        currentNights: d.nights ? String(d.nights) : "",
        newCheckIn: "",
        newCheckOut: "",
        newNights: "",
        // Hotel room — try every possible source for the current room/board description
        currentRoom: d.roomName || firstRoom.roomName || firstRoom.roomType || d.roomType || "—",
        newRoom: "",
        currentBoard: d.boardName || d.boardCode || firstRoom.boardName || firstRoom.boardCode || "—",
        newBoard: "",
        newRoomType: firstRoom.roomType || d.roomType || "",
        newRoomCharacteristic: firstRoom.roomCharacteristic || d.roomCharacteristic || "",
        newRoomStatus: firstRoom.roomStatus || d.roomStatus || "",
        newRoomRate: firstRoom.roomRate || d.roomRate || "Refundable",
        newNumberOfRooms: String(d.numberOfRooms || rooms.length || 1),
        newBoardCode: firstRoom.boardCode || d.boardCode || "",
        newBoardName: firstRoom.boardName || d.boardName || "",
        newRoomName: firstRoom.roomName || d.roomName || "",
        extraNights: "1",
        currentCount: String(component?.travelersCount || 0),
        newCount: "",
        noShowDate: firstLeg.date || firstLeg.departureDate || d.checkIn || "",
        refundReason: "",
        recoveryReason: "",
      }));
    }
  }, [open, actionType]);

  const updateLegEdit = (idx, field, value) => {
    setForm((prev) => {
      const updated = [...prev.legEdits];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, legEdits: updated };
    });
  };

  const totalAmount = (parseFloat(form.supplierCost || 0) + parseFloat(form.agencyFee || 0) - parseFloat(form.refundAmount || 0)).toFixed(2);
  const fields = config.fields || [];

  if (!open) return null;

  return (
    <BaseModal open={open} onClose={onClose} title={actionType || "Post-Booking Modification"} width="900px">
      <div className={styles.modalBody}>
        {/* Action Header */}
        <div className={styles.pbActionHeader} style={{ borderLeftColor: config.color }}>
          <ActionIcon size={16} style={{ color: config.color }} />
          <div>
            <div className={styles.pbActionTitle}>{actionType}</div>
            <div className={styles.pbActionDesc}>{config.description}</div>
          </div>
        </div>

        {/* ── Flight & Leg Selector (flight post-booking actions only) ── */}
        {isFlightAction && !skipLegSelector && legs.length > 1 && (
          <div className={styles.flsContainer}>
            <div className={styles.flsHeader}>
              <Plane size={14} className={styles.flsHeaderIcon} />
              <span className={styles.flsHeaderTitle}>Select Flight & Leg</span>
              <span className={styles.flsHeaderHint}>Choose which flight direction and leg this action applies to</span>
            </div>

            {/* Direction Buttons */}
            <div className={styles.flsDirectionRow}>
              {directionOptions.map((dir) => {
                const isActive = selectedDirection === dir;
                const count = dir === "Both" ? legs.length : (legGroups[dir] || []).length;
                return (
                  <button
                    key={dir}
                    className={`${styles.flsDirectionBtn} ${isActive ? styles.flsDirectionBtnActive : ""}`}
                    onClick={() => setSelectedDirection(dir)}
                  >
                    <span className={styles.flsDirectionLabel}>{dir}</span>
                    <span className={styles.flsDirectionCount}>{count} leg{count !== 1 ? "s" : ""}</span>
                  </button>
                );
              })}
            </div>

            {/* Leg List for selected direction */}
            {selectedDirection && displayLegs.length > 0 && (
              <div className={styles.flsLegList}>
                {displayLegs.map(({ leg, index }) => {
                  const depCode = (leg.departure || leg.departureAirport || "").toUpperCase();
                  const arrCode = (leg.arrival || leg.arrivalAirport || "").toUpperCase();
                  const depName = airportMap[depCode] || depCode;
                  const arrName = airportMap[arrCode] || arrCode;
                  const legDate = leg.date || leg.departureDate || "";
                  const fCode = `${leg.airlineCode || ""}${leg.flightNumber || ""}`;
                  const isSelected = selectedLegIndices.includes(index);
                  return (
                    <div
                      key={index}
                      className={`${styles.flsLegItem} ${isSelected ? styles.flsLegItemActive : ""}`}
                      onClick={() => {
                        setSelectedLegIndices((prev) =>
                          prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
                        );
                      }}
                    >
                      <span className={styles.flsLegCheck}>
                        {isSelected ? <Check size={13} /> : <span className={styles.flsLegCheckEmpty} />}
                      </span>
                      <span className={styles.flsLegNum}>{index + 1}.</span>
                      <span className={styles.flsLegDate}>{formatDate(legDate)}</span>
                      <span className={styles.flsLegRoute}>
                        <span className={styles.flsLegCode}>{depCode}</span>
                        <span className={styles.flsLegAirportName}>({depName})</span>
                        <ArrowRight size={12} className={styles.flsLegArrow} />
                        <span className={styles.flsLegCode}>{arrCode}</span>
                        <span className={styles.flsLegAirportName}>({arrName})</span>
                      </span>
                      {fCode && <span className={styles.flsLegFlight}>{fCode}</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* No selection hint */}
            {!selectedDirection && (
              <div className={styles.flsHint}>
                <AlertTriangle size={13} />
                Select a flight direction above to continue
              </div>
            )}
          </div>
        )}

        {/* ── Per-Leg Fields (Date, Time, Route, Flight Code) ── */}
        {(fields.includes("currentDate") || fields.includes("newDate") || fields.includes("currentRoute") || fields.includes("newRoute") || fields.includes("newFlightCode") || fields.includes("newTime")) && form.legEdits
          .map((le, idx) => ({ le, idx }))
          .filter(({ idx }) => !isFlightAction || selectedLegIndices.includes(idx))
          .map(({ le, idx }) => (
          <div key={idx} className={styles.pbFieldBlock}>
            {form.legEdits.length > 1 && (
              <div className={styles.pbFieldBlockTitle} style={{ fontWeight: 600, marginBottom: 6 }}>
                {le.direction} — {le.currentFrom} → {le.currentTo}
              </div>
            )}

            {(fields.includes("currentDate") || fields.includes("newDate")) && (
              <>
                <div className={styles.pbFieldBlockTitle}>Date Details</div>
                <div className={styles.formRow}>
                  {fields.includes("currentDate") && (
                    <div className={styles.formGroup}>
                      <label>Current Date</label>
                      <input type="date" value={le.currentDate} disabled className={styles.readonlyInput} />
                    </div>
                  )}
                  {fields.includes("newDate") && (
                    <div className={styles.formGroup}>
                      <label>New Date <span className={styles.required}>*</span></label>
                      <input type="date" value={le.newDate} onChange={(e) => updateLegEdit(idx, "newDate", e.target.value)} />
                    </div>
                  )}
                </div>
              </>
            )}

            {fields.includes("newTime") && (
              <>
                <div className={styles.pbFieldBlockTitle}>Time Details</div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Current Departure Time</label>
                    <input type="time" value={le.currentDepartureTime} disabled className={styles.readonlyInput} />
                  </div>
                  <div className={styles.formGroup}>
                    <label>New Departure Time</label>
                    <input type="time" value={le.newDepartureTime} onChange={(e) => updateLegEdit(idx, "newDepartureTime", e.target.value)} />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Current Arrival Time</label>
                    <input type="time" value={le.currentArrivalTime} disabled className={styles.readonlyInput} />
                  </div>
                  <div className={styles.formGroup}>
                    <label>New Arrival Time</label>
                    <input type="time" value={le.newArrivalTime} onChange={(e) => updateLegEdit(idx, "newArrivalTime", e.target.value)} />
                  </div>
                </div>
              </>
            )}

            {(fields.includes("currentRoute") || fields.includes("newRoute")) && (
              <>
                <div className={styles.pbFieldBlockTitle}>Route Details</div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Current From</label>
                    <input type="text" value={`${le.currentFrom}${airportMap[le.currentFrom] ? ` — ${airportMap[le.currentFrom]}` : ""}`} disabled className={styles.readonlyInput} />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Current To</label>
                    <input type="text" value={`${le.currentTo}${airportMap[le.currentTo] ? ` — ${airportMap[le.currentTo]}` : ""}`} disabled className={styles.readonlyInput} />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>New From <span className={styles.required}>*</span></label>
                    <SearchableSelect
                      value={le.newFrom}
                      onChange={(val) => updateLegEdit(idx, "newFrom", val)}
                      options={airportOptions}
                      onSearch={handleAirportSearch}
                      placeholder="Select airport..."
                      searchPlaceholder="Type 2+ chars to search..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>New To <span className={styles.required}>*</span></label>
                    <SearchableSelect
                      value={le.newTo}
                      onChange={(val) => updateLegEdit(idx, "newTo", val)}
                      options={airportOptions}
                      onSearch={handleAirportSearch}
                      placeholder="Select airport..."
                      searchPlaceholder="Type 2+ chars to search..."
                    />
                  </div>
                </div>
              </>
            )}

            {fields.includes("newFlightCode") && (
              <>
                <div className={styles.pbFieldBlockTitle}>Flight Details</div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Current Airline</label>
                    <input type="text" value={`${le.currentAirlineCode}${le.currentAirlineName ? ` — ${le.currentAirlineName}` : ""}`} disabled className={styles.readonlyInput} />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Current Flight Code</label>
                    <input type="text" value={le.currentFlightCode} disabled className={styles.readonlyInput} />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>New Airline</label>
                    <SearchableSelect
                      value={le.newAirlineCode}
                      onChange={(val) => {
                        const opt = airlineOptions.find((o) => o.value === val);
                        updateLegEdit(idx, "newAirlineCode", val);
                        updateLegEdit(idx, "newAirlineName", opt?.name || "");
                      }}
                      options={airlineOptions}
                      onSearch={handleAirlineSearch}
                      placeholder="Search airline..."
                      searchPlaceholder="Type 2+ chars to search..."
                      initialLabel={le.newAirlineName ? `${le.newAirlineCode} — ${le.newAirlineName}` : le.newAirlineCode}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>New Airline Code</label>
                    <input type="text" maxLength={3} value={le.newAirlineCode} onChange={(e) => updateLegEdit(idx, "newAirlineCode", e.target.value.toUpperCase())} placeholder={le.currentAirlineCode ? `Current: ${le.currentAirlineCode}` : "e.g. XQ"} style={{ textTransform: "uppercase" }} />
                  </div>
                  <div className={styles.formGroup}>
                    <label>New Flight Code</label>
                    <input type="text" value={le.newFlightCode} onChange={(e) => updateLegEdit(idx, "newFlightCode", e.target.value.toUpperCase())} placeholder={le.currentFlightCode ? `Current: ${le.currentFlightCode}` : "e.g. 959"} style={{ textTransform: "uppercase" }} />
                  </div>
                </div>
              </>
            )}
          </div>
        ))}

        {/* ── Hotel Date Modification ── */}
        {fields.includes("hotelDates") && (
          <div className={styles.pbFieldBlock}>
            <div className={styles.pbFieldBlockTitle}>Current Stay</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Check-In</label>
                <input type="date" value={form.currentCheckIn} disabled className={styles.readonlyInput} />
              </div>
              <div className={styles.formGroup}>
                <label>Check-Out</label>
                <input type="date" value={form.currentCheckOut} disabled className={styles.readonlyInput} />
              </div>
              <div className={styles.formGroup}>
                <label>Nights</label>
                <input type="text" value={form.currentNights} disabled className={styles.readonlyInput} />
              </div>
            </div>
            <div className={styles.pbFieldBlockTitle} style={{ marginTop: 12 }}>New Dates</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>New Check-In <span className={styles.required}>*</span></label>
                <input type="date" value={form.newCheckIn} onChange={(e) => {
                  const ci = e.target.value;
                  const nights = recalcNights(ci, form.newCheckOut);
                  setForm({ ...form, newCheckIn: ci, newNights: nights });
                }} />
              </div>
              <div className={styles.formGroup}>
                <label>New Check-Out <span className={styles.required}>*</span></label>
                <input type="date" value={form.newCheckOut} onChange={(e) => {
                  const co = e.target.value;
                  const nights = recalcNights(form.newCheckIn, co);
                  setForm({ ...form, newCheckOut: co, newNights: nights });
                }} />
              </div>
              <div className={styles.formGroup}>
                <label>Nights</label>
                <input type="text" value={form.newNights} disabled className={styles.readonlyInput} />
              </div>
            </div>
          </div>
        )}

        {/* ── Hotel Room Change (full details) ── */}
        {fields.includes("roomDetails") && (
          <div className={styles.pbFieldBlock}>
            <div className={styles.pbFieldBlockTitle}>Current Room</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Room</label>
                <input type="text" value={form.currentRoom} disabled className={styles.readonlyInput} />
              </div>
              <div className={styles.formGroup}>
                <label>Board</label>
                <input type="text" value={form.currentBoard} disabled className={styles.readonlyInput} />
              </div>
            </div>
            <div className={styles.pbFieldBlockTitle} style={{ marginTop: 12 }}>New Room Details</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Room Name</label>
                <input type="text" value={form.newRoomName || ""} onChange={(e) => setForm({ ...form, newRoomName: e.target.value })} placeholder="e.g. Deluxe Double" />
              </div>
              <div className={styles.formGroup}>
                <label>Room Type <span className={styles.required}>*</span></label>
                <SearchableListDropdown items={ROOM_TYPES_FULL} value={form.newRoomType} onChange={(v) => setForm({ ...form, newRoomType: v })} placeholder="Select room type…" searchPlaceholder="Search room types…" allowCustom />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Room Characteristic</label>
                <SearchableListDropdown items={ROOM_CHARS_FULL} value={form.newRoomCharacteristic} onChange={(v) => setForm({ ...form, newRoomCharacteristic: v })} placeholder="Select characteristic…" searchPlaceholder="Search views, features, amenities…" allowCustom />
              </div>
              <div className={styles.formGroup}>
                <label>Number of Rooms</label>
                <input type="number" min="1" value={form.newNumberOfRooms} onChange={(e) => setForm({ ...form, newNumberOfRooms: e.target.value })} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Board <span className={styles.required}>*</span></label>
                <BoardCodeDropdown value={form.newBoardCode} onChange={(code) => setForm({ ...form, newBoardCode: code, newBoardName: BOARD_CODE_TO_NAME[code] || code })} placeholder="Select board type…" allowCustom />
              </div>
              <div className={styles.formGroup}>
                <label>Room Rate</label>
                <select value={form.newRoomRate} onChange={(e) => setForm({ ...form, newRoomRate: e.target.value })}>
                  <option value="Refundable">Refundable</option>
                  <option value="Non-Refundable">Non-Refundable</option>
                  <option value="Partially Refundable">Partially Refundable</option>
                </select>
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Room Status</label>
                <select value={form.newRoomStatus} onChange={(e) => setForm({ ...form, newRoomStatus: e.target.value })}>
                  <option value="">Select...</option>
                  <option value="Available">Available</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="On Request">On Request</option>
                  <option value="Waitlist">Waitlist</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ── Simple Room Change (legacy) ── */}
        {(fields.includes("currentRoom") || fields.includes("newRoom")) && (
          <div className={styles.pbFieldBlock}>
            <div className={styles.pbFieldBlockTitle}>Room Details</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Current Room</label>
                <input type="text" value={form.currentRoom} disabled className={styles.readonlyInput} />
              </div>
              <div className={styles.formGroup}>
                <label>New Room <span className={styles.required}>*</span></label>
                <input type="text" value={form.newRoom} onChange={(e) => setForm({ ...form, newRoom: e.target.value })} placeholder="New room type" />
              </div>
            </div>
          </div>
        )}

        {/* ── Board Type Change Fields ── */}
        {(fields.includes("currentBoard") || fields.includes("newBoard")) && (
          <div className={styles.pbFieldBlock}>
            <div className={styles.pbFieldBlockTitle}>Board / Meal Plan</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Current Board</label>
                <input type="text" value={form.currentBoard} disabled className={styles.readonlyInput} />
              </div>
              <div className={styles.formGroup}>
                <label>New Board <span className={styles.required}>*</span></label>
                <BoardCodeDropdown value={form.newBoard} onChange={(code) => setForm({ ...form, newBoard: code })} placeholder="Select new board…" allowCustom />
              </div>
            </div>
          </div>
        )}

        {/* ── Extra Nights ── */}
        {fields.includes("extraNights") && (
          <div className={styles.pbFieldBlock}>
            <div className={styles.pbFieldBlockTitle}>Extra Nights</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Number of Extra Nights</label>
                <input type="number" min="1" value={form.extraNights} onChange={(e) => setForm({ ...form, extraNights: e.target.value })} />
              </div>
              <div className={styles.formGroup}>
                <label>New Check-out Date</label>
                <input type="date" value={form.newDate} onChange={(e) => setForm({ ...form, newDate: e.target.value })} />
              </div>
            </div>
          </div>
        )}

        {/* ── Traveler Count Change ── */}
        {(fields.includes("currentCount") || fields.includes("newCount")) && (
          <div className={styles.pbFieldBlock}>
            <div className={styles.pbFieldBlockTitle}>Traveler Count</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Current Count</label>
                <input type="text" value={form.currentCount} disabled className={styles.readonlyInput} />
              </div>
              <div className={styles.formGroup}>
                <label>New Count <span className={styles.required}>*</span></label>
                <input type="number" min="1" value={form.newCount} onChange={(e) => setForm({ ...form, newCount: e.target.value })} placeholder="New count" />
              </div>
            </div>
          </div>
        )}

        {/* ── No-Show Date + Pricing Breakdown ── */}
        {fields.includes("noShowDate") && (
          <div className={styles.pbFieldBlock}>
            <div className={styles.pbFieldBlockTitle}>No-Show Details</div>
            <div className={styles.formGroup}>
              <label>No-Show Date</label>
              <input
                type="date"
                value={form.noShowDate}
                onChange={(e) => setForm({ ...form, noShowDate: e.target.value })}
              />
            </div>

            {/* Pricing context so the agent knows what refund to offer */}
            {(() => {
              const roomPricing = d.pricing?.roomPricing || [];
              const totalSell = roomPricing.reduce((s, rp) => s + parseFloat(rp.sellingPrice || 0), 0);
              const totalCost = roomPricing.reduce((s, rp) => s + parseFloat(rp.netPrice || 0), 0);
              const hasPricing = totalSell > 0 || totalCost > 0 || d.checkIn;
              if (!hasPricing) return null;
              return (
                <div className={styles.noShowPricingBreakdown}>
                  <div className={styles.pbFieldBlockTitle} style={{ marginTop: 12 }}>
                    Current Booking Snapshot
                  </div>
                  <div className={styles.noShowGrid}>
                    {d.checkIn && (
                      <div className={styles.noShowRow}>
                        <span className={styles.noShowLabel}>Check-In</span>
                        <span className={styles.noShowValue}>{d.checkIn}</span>
                      </div>
                    )}
                    {d.checkOut && (
                      <div className={styles.noShowRow}>
                        <span className={styles.noShowLabel}>Check-Out</span>
                        <span className={styles.noShowValue}>{d.checkOut}</span>
                      </div>
                    )}
                    {d.nights != null && (
                      <div className={styles.noShowRow}>
                        <span className={styles.noShowLabel}>Nights</span>
                        <span className={styles.noShowValue}>{d.nights}</span>
                      </div>
                    )}
                    {roomPricing.map((rp, idx) => (
                      <div key={idx} className={styles.noShowRow} style={{ borderTop: idx === 0 ? "1px solid var(--border-subtle)" : undefined, marginTop: idx === 0 ? 8 : 0, paddingTop: idx === 0 ? 8 : 0 }}>
                        <span className={styles.noShowLabel}>{rp.description || `Modification #${rp.modificationId || idx + 1}`}</span>
                        <span className={styles.noShowValue}>
                          Client: <strong>€ {parseFloat(rp.sellingPrice || 0).toFixed(2)}</strong>
                          &nbsp;/ Cost: € {parseFloat(rp.netPrice || 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {totalSell > 0 && (
                      <div className={styles.noShowRow} style={{ borderTop: "1px solid var(--border-subtle)", marginTop: 8, paddingTop: 8, fontWeight: 600 }}>
                        <span className={styles.noShowLabel}>Total Paid by Client</span>
                        <span className={styles.noShowValue}>€ {totalSell.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
                    Use the figures above to determine the appropriate refund amount below.
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Refund Reason ── */}
        {fields.includes("refundReason") && (
          <div className={styles.formGroup}>
            <label>Refund Reason <span className={styles.required}>*</span></label>
            <textarea rows={2} value={form.refundReason} onChange={(e) => setForm({ ...form, refundReason: e.target.value })} placeholder="Reason for refund request..." />
          </div>
        )}

        {/* ── Recovery Reason ── */}
        {fields.includes("recoveryReason") && (
          <div className={styles.formGroup}>
            <label>Recovery Reason <span className={styles.required}>*</span></label>
            <textarea rows={2} value={form.recoveryReason} onChange={(e) => setForm({ ...form, recoveryReason: e.target.value })} placeholder="Reason for cost recovery..." />
          </div>
        )}

        {/* ── Common: Title & Notes ── */}
        <div className={styles.formGroup}>
          <label>Modification Title <span className={styles.optional}>(optional)</span></label>
          <input type="text" value={form.modificationTitle} onChange={(e) => setForm({ ...form, modificationTitle: e.target.value })} placeholder="Brief title for this modification" />
        </div>

        <div className={styles.formGroup}>
          <label>Internal Notes <span className={styles.optional}>(optional)</span></label>
          <textarea rows={2} value={form.internalNotes} onChange={(e) => setForm({ ...form, internalNotes: e.target.value })} placeholder="Internal notes..." />
        </div>

        <div className={styles.separator} />

        {/* ── Financial Impact ── */}
        <div className={styles.financialSection}>
          <div className={styles.financialTitle}>Financial Impact</div>
          <div className={styles.formRow}>
            {config.financialFocus !== "refund" && (
              <div className={styles.formGroup}>
                <label>{config.financialFocus === "recovery" ? "Recovery Amount" : "Supplier Cost"}</label>
                <input type="number" step="0.01" value={form.supplierCost} onChange={(e) => setForm({ ...form, supplierCost: e.target.value })} placeholder="0.00" />
              </div>
            )}
            {config.financialFocus !== "refund" && config.financialFocus !== "recovery" && (
              <div className={styles.formGroup}>
                <label>Agency Fee</label>
                <input type="number" step="0.01" value={form.agencyFee} onChange={(e) => setForm({ ...form, agencyFee: e.target.value })} placeholder="0.00" />
              </div>
            )}
            <div className={styles.formGroup}>
              <label>Refund Amount</label>
              <input type="number" step="0.01" value={form.refundAmount} onChange={(e) => setForm({ ...form, refundAmount: e.target.value })} placeholder="0.00" />
            </div>
          </div>
          <div className={styles.totalRow}>
            <span>Total Modification Amount</span>
            <span className={styles.totalValue}>€ {totalAmount}</span>
          </div>
          <div className={styles.vatRow}>
            <span>VAT</span>
            <span className={styles.vatAuto}>(auto recalculated)</span>
          </div>
        </div>

        <div className={styles.separator} />

        {/* ── Document & Communication ── */}
        <div className={styles.docComSection}>
          <div className={styles.financialTitle}>Document & Communication</div>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" checked={form.regenerateDocuments} onChange={(e) => setForm({ ...form, regenerateDocuments: e.target.checked })} />
            Regenerate Documents
          </label>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" checked={form.notifyCustomer} onChange={(e) => setForm({ ...form, notifyCustomer: e.target.checked })} />
            Notify Customer
          </label>
        </div>

        <div className={styles.modalActions}>
          <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button
            className={styles.btnPrimary}
            disabled={isFlightAction && !skipLegSelector && selectedLegIndices.length === 0}
            onClick={() => {
              onConfirm({
                ...form,
                selectedDirection: selectedDirection || null,
                selectedLegIndices: selectedLegIndices.length > 0 ? selectedLegIndices : undefined,
              });
              onClose();
            }}
          >
            <Check size={13} /> Save Modification
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

/* ══════════════════════════════════════════════
   SYNC FLIGHT STATUS MODAL
   Asks the supplier whether this flight's schedule has moved.

   This modal previously FAKED the check: a 1.5s timer that always reported
   "No schedule changes detected. Flight status is up to date." It contacted
   nothing. Its "changes detected" branch was worse — it invented the supplier
   times by rewriting the minutes to :45/:40 and labelled them "Supplier API
   Details".

   Spec §21 is explicit: an API failure must NEVER be reported as NO_CHANGE,
   because SUNSKY does not know whether the schedule is still correct. It now
   calls the real endpoint and reports exactly what came back — including
   "not configured", which is today's honest answer.
   ══════════════════════════════════════════════ */
const SyncFlightModal = ({ open, onClose, component, onManualChange }) => {
  const { execute: checkSchedule, loading: syncing } = useCheckScheduleApi();
  const [result, setResult] = useState(null);
  const raw = component?._raw || {};
  const d = raw.productDetails || {};
  const legs = d.legs || [];
  const firstLeg = legs[0] || {};

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setResult(null);
    checkSchedule().then((res) => {
      if (!cancelled) setResult(res);
    });
    return () => { cancelled = true; };
  }, [open, checkSchedule]);

  if (!open) return null;

  const unavailable = result && !result.success;

  return (
    <BaseModal open={open} onClose={onClose} title="Sync Flight Status" width="480px">
      <div className={styles.modalBody}>
        {/* Contacting the supplier */}
        {syncing && (
          <div className={styles.syncLoading}>
            <RefreshCw size={20} className={styles.spinning} />
            <span>Checking with the supplier…</span>
          </div>
        )}

        {/* No supplier schedule-check integration exists (§21). Say so plainly —
            reporting "no changes" here would be a guess presented as a fact. */}
        {unavailable && (
          <>
            <div className={styles.syncUnavailable}>
              <AlertTriangle size={22} className={styles.syncWarnIcon} />
              <div className={styles.syncUnavailableTitle}>Schedule not verified</div>
              <div className={styles.syncUnavailableText}>{result.message}</div>
            </div>
            <div className={styles.syncFlightRef}>
              <span className={styles.airportBig}>{(firstLeg.departure || firstLeg.departureAirport || "").toUpperCase()}</span>
              <ArrowRight size={14} className={styles.routeArrow} />
              <span className={styles.airportBig}>{(firstLeg.arrival || firstLeg.arrivalAirport || "").toUpperCase()}</span>
              <span className={styles.syncFlightMeta}>
                {formatDate(firstLeg.date || firstLeg.departureDate)} &middot;{" "}
                {firstLeg.time || firstLeg.departureTime || "—"} → {firstLeg.arrivalTime || "—"} &middot;{" "}
                {firstLeg.airlineCode || ""}{firstLeg.flightNumber || ""}
              </span>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={onClose}>Close</button>
              <button className={styles.btnPrimary} onClick={() => { onClose(); onManualChange?.(); }}>
                <Pencil size={13} /> Record change manually
              </button>
            </div>
          </>
        )}

        {/* A real NO_CHANGE — only reachable once a supplier check exists. */}
        {result && result.success && (
          <>
            <div className={styles.syncNoChange}>
              <Check size={24} className={styles.syncCheckIcon} />
              <div className={styles.syncNoChangeTitle}>{result.message}</div>
              <div className={styles.syncNoChangeText}>Checked just now against the supplier.</div>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
};

/* ══════════════════════════════════════════════
   SCHEDULE CHANGE HISTORY (inline section)
   ══════════════════════════════════════════════ */
/**
 * Map a stored bookingScheduleChanges row to the shape this section renders.
 *
 * The record holds full before/after leg snapshots. Only the leg that actually
 * moved is worth showing, so the two snapshots are diffed and the first
 * genuinely changed pair is surfaced — matched by leg id, since a date change
 * can reorder the array (legs are stored chronologically).
 */
const mapScheduleChange = (row) => {
  const prevLegs = row?.previousSchedule?.legs || [];
  const nextLegs = row?.updatedSchedule?.legs || [];

  const face = (leg) => leg && ({
    from: (leg.departureAirport || leg.departure || "").toUpperCase(),
    to: (leg.arrivalAirport || leg.arrival || "").toUpperCase(),
    date: formatDate(leg.departureDate || leg.date || ""),
    depTime: leg.departureTime || leg.time || "",
    arrTime: leg.arrivalTime || "",
    flightCode: `${leg.airlineCode || ""}${leg.flightNumber || ""}`,
  });

  const differs = (a, b) =>
    a && b && ["from", "to", "date", "depTime", "arrTime", "flightCode"].some((k) => a[k] !== b[k]);

  let prev = null;
  let updated = null;
  for (const before of prevLegs) {
    const after = nextLegs.find((l) => String(l.id) === String(before.id));
    const a = face(before);
    const b = face(after);
    if (differs(a, b)) { prev = a; updated = b; break; }
  }
  // Fall back to the first leg pair when nothing diffable was found, so a record
  // never renders as an empty card.
  if (!prev) { prev = face(prevLegs[0]); updated = face(nextLegs[0]); }

  return {
    title: `Schedule Change – ${formatDate(row.detectedAt)}`,
    prev,
    updated,
    detectedVia: row.detectedVia,
    detectedAt: formatDate(row.detectedAt),
    updatedBy: row.updatedBy ? `User #${row.updatedBy}` : "—",
    customerNotified: row.customerNotified,
    docsRegenerated: row.docsRegenerated,
    impact: row.impact,
    resolved: row.resolved,
    notes: row.notes,
  };
};

/**
 * The history cards themselves — deliberately unchanged.
 *
 * Split out of ScheduleChangeHistory so the exact same markup renders inside the
 * modal. Nothing about a card's look moves with it; only its container did.
 */
const ScheduleChangeList = ({ changes }) => {
  return (
    <div className={styles.historyList}>
        {changes.map((change, idx) => (
          <div key={idx} className={styles.historyCard}>
            <div className={styles.historyCardHeader}>
              <span className={styles.historyDate}>{change.title || `Schedule Change – ${change.detectedAt || "—"}`}</span>
              <span className={`${styles.historyImpact} ${styles[`impact${change.impact || "Minor"}`]}`}>
                {change.impact || "Minor"}
              </span>
            </div>
            <div className={styles.historyCompare}>
              <div className={styles.historyBlock}>
                <div className={styles.historyBlockLabel}>Previous Schedule</div>
                <div className={styles.historyRoute}>
                  {change.prev?.from || "—"} → {change.prev?.to || "—"}
                </div>
                <div className={styles.historyMeta}>
                  {change.prev?.date || "—"} &middot; {change.prev?.depTime || "—"} → {change.prev?.arrTime || "—"} &middot; {change.prev?.flightCode || "—"}
                </div>
              </div>
              <div className={styles.historyArrow}><ArrowRight size={16} /></div>
              <div className={styles.historyBlock}>
                <div className={styles.historyBlockLabel}>Updated Schedule</div>
                <div className={styles.historyRoute}>
                  {change.updated?.from || "—"} → {change.updated?.to || "—"}
                </div>
                <div className={styles.historyMeta}>
                  {change.updated?.date || "—"} &middot; <span className={styles.changedValue}>{change.updated?.depTime || "—"} → {change.updated?.arrTime || "—"}</span> &middot; {change.updated?.flightCode || "—"}
                </div>
              </div>
            </div>
            <div className={styles.historyFooter}>
              <div className={styles.historyFooterItem}>
                <span className={styles.historyFooterLabel}>Detected via:</span> {change.detectedVia || "—"}
              </div>
              <div className={styles.historyFooterItem}>
                <span className={styles.historyFooterLabel}>Detected at:</span> {change.detectedAt || "—"}
              </div>
              <div className={styles.historyFooterItem}>
                <span className={styles.historyFooterLabel}>Updated by:</span> {change.updatedBy || "—"}
              </div>
              <div className={styles.historyFooterItem}>
                <span className={styles.historyFooterLabel}>Customer notified:</span> {change.customerNotified ? "Yes" : "No"}
              </div>
              <div className={styles.historyFooterItem}>
                <span className={styles.historyFooterLabel}>Documents regenerated:</span> {change.docsRegenerated ? "Yes" : "No"}
              </div>
            </div>
            {/* An unresolved change still needs the customer told and the
                documents reissued. Those are stated as outstanding rather than
                offered as buttons — neither is built yet (§43, §47), and a
                button that does nothing is worse than no button. */}
            {!change.resolved && (
              <div className={styles.historyOutstanding}>
                <AlertTriangle size={12} />
                Outstanding: customer notification and document reissue are not
                automated yet — handle them manually.
              </div>
            )}
          </div>
        ))}
      </div>
  );
};

/**
 * Schedule change history, behind a button (§42).
 *
 * The list used to sit open in the flight card and pushed everything below it
 * down — on a flight with several changes the itinerary itself scrolled off.
 * The button carries the count so the history is still visible AS A FACT
 * without costing the space; the cards inside are byte-for-byte the same.
 *
 * Renders nothing at all when there are no changes: an empty history is not
 * worth a button.
 */
const ScheduleChangeHistory = ({ changes }) => {
  const [open, setOpen] = useState(false);
  if (!changes || changes.length === 0) return null;

  const unresolved = changes.filter((c) => !c.resolved).length;

  return (
    <>
      <div className={styles.historyTrigger}>
        <button type="button" className={styles.historyTriggerBtn} onClick={() => setOpen(true)}>
          <Clock size={14} />
          Schedule Change History
          <span className={styles.historyTriggerCount}>{changes.length}</span>
          {/* An unresolved change still needs the customer told and the
              documents reissued — worth seeing before opening. */}
          {unresolved > 0 && (
            <span className={styles.historyTriggerOutstanding}>
              {unresolved} outstanding
            </span>
          )}
        </button>
      </div>

      <BaseModal
        open={open}
        onClose={() => setOpen(false)}
        title="Schedule Change History"
        width="760px"
      >
        <div className={styles.modalBody}>
          <ScheduleChangeList changes={changes} />
          <div className={styles.modalActions}>
            <button className={styles.btnSecondary} onClick={() => setOpen(false)}>Close</button>
          </div>
        </div>
      </BaseModal>
    </>
  );
};

/* ══════════════════════════════════════════════
   FLIGHT CARD
   ══════════════════════════════════════════════ */
const FlightCard = ({ component, isLocked, onEdit, onDelete, bookingId, onRefresh, airportMap = {}, onOpenSSR, canc }) => {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleLegIdx, setScheduleLegIdx] = useState(0);
  const [syncOpen, setSyncOpen] = useState(false);
  const [postBookingOpen, setPostBookingOpen] = useState(false);
  const [postBookingAction, setPostBookingAction] = useState("");
  const actionsRef = useRef(null);

  const { showToast } = useToast();
  const copyApi = useCopyProduct();
  const removeValidateApi = useValidateProductRemoval();
  const deleteApi = useDeleteBookingProduct();
  const modificationApi = useCreateProductModification();
  const scheduleChangeApi = useCreateScheduleChange();

  // §42 — recorded schedule changes for this flight. The section was written
  // but commented out and only ever fed demo data, so every real change written
  // to bookingScheduleChanges has been invisible.
  const getScheduleChangesApi = useGetScheduleChanges();
  const [scheduleChanges, setScheduleChanges] = useState([]);

  const loadScheduleChanges = useCallback(async () => {
    if (!bookingId || !component?.id) return;
    const res = await getScheduleChangesApi.execute(bookingId, component.id);
    if (res.success && Array.isArray(res.data)) {
      setScheduleChanges(res.data.map(mapScheduleChange));
    }
  }, [bookingId, component?.id, getScheduleChangesApi]);

  useEffect(() => { loadScheduleChanges(); }, [loadScheduleChanges]);

  const closeActions = useCallback(() => setActionsOpen(false), []);
  useClickOutside(actionsRef, closeActions);

  const raw = component._raw || {};
  const d = raw.productDetails || {};
  const legs = d.legs || [];
  const ctx = raw.booking_context || raw.bookingContext || "";
  const flightType = d.flightType || "OW";
  // A round trip the customer asked for (a return date) with no return leg held: the outbound
  // alone was booked (booking 334, 5 Nov 2026). Shown as an Inbound "Not booked" block, and the
  // flight reads "Partially Confirmed", never plain "Confirmed".
  const missingReturn = (() => {
    const ret = String(d.retdate || d.returnDate || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ret) || !legs.length || legs.some(isReturnLeg)) return null;
    if (legs.some((l) => String(l.departureDate || l.date || "").slice(0, 10) >= ret)) return null;
    const first = legs[0] || {};
    const last = legs[legs.length - 1] || {};
    return {
      date: ret,
      from: String(last.arrivalAirport || last.arrival || last.to || "").toUpperCase(),
      to: String(first.departureAirport || first.departure || first.from || "").toUpperCase(),
    };
  })();
  const shownStatus = missingReturn && component.status === "Confirmed" ? "Partially Confirmed" : component.status;
  const tc = component.travelersCount || 0;

  const openPostBooking = (action) => {
    setPostBookingAction(action);
    setPostBookingOpen(true);
    setActionsOpen(false);
  };

  const handleCopy = async (formData) => {
    if (!bookingId) return;
    const result = await copyApi.execute(bookingId, component.id, {
      supplierReference: formData.supplierRef,
      pnrs: formData.pnrs || {},
    });
    if (result.success) { showToast("Flight copied successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to copy flight", "error");
  };

  const handleRemoveConfirm = async () => {
    if (!bookingId) return;
    const result = await deleteApi.execute(bookingId, component.id);
    if (result.success) { showToast("Flight removed successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to remove flight", "error");
  };

  const handleScheduleUpdate = async (formData) => {
    if (!bookingId) return;
    const prevLegs = legs.map((leg) => ({
      departureAirport: leg.departure || leg.departureAirport,
      arrivalAirport: leg.arrival || leg.arrivalAirport,
      departureDate: leg.date || leg.departureDate,
      departureTime: leg.time || leg.departureTime,
      arrivalDate: leg.arrivalDate,
      arrivalTime: leg.arrivalTime,
      flightCode: `${leg.airlineCode || ""}${leg.flightNumber || ""}`,
    }));
    const updatedLegs = (formData.legForms || []).map((f) => ({
      departureAirport: f.departureAirport,
      arrivalAirport: f.arrivalAirport,
      departureDate: f.departureDate,
      departureTime: f.departureTime,
      arrivalDate: f.arrivalDate,
      arrivalTime: f.arrivalTime,
      flightCode: f.flightCode,
    }));
    const result = await scheduleChangeApi.execute(bookingId, component.id, {
      previousSchedule: { legs: prevLegs },
      updatedSchedule: { legs: updatedLegs },
      impact: formData.changeImpact,
      detectedVia: formData.detectedVia || "Manual",
      notes: formData.notes,
      customerNotified: formData.customerNotified,
      docsRegenerated: formData.docsRegenerated,
    });
    if (result.success) {
      showToast("Schedule change recorded", "success");
      loadScheduleChanges(); // §42 — surface it in the history immediately
      onRefresh?.();
    }
    else showToast(result.message || "Failed to update schedule", "error");
  };

  const handlePostBooking = async (formData) => {
    if (!bookingId) return;
    const result = await modificationApi.execute(bookingId, component.id, {
      modificationType: formData.modificationType || "OTHER",
      modificationTitle: formData.modificationTitle || postBookingAction,
      internalNotes: formData.internalNotes,
      supplierCost: formData.supplierCost,
      agencyFee: formData.agencyFee,
      refundAmount: formData.refundAmount,
      regenerateDocuments: formData.regenerateDocuments,
      notifyCustomer: formData.notifyCustomer,
      changeData: {
        legEdits: formData.legEdits,
        newRoom: formData.newRoom,
        newBoard: formData.newBoard,
        extraNights: formData.extraNights,
        newCount: formData.newCount,
        noShowDate: formData.noShowDate,
        refundReason: formData.refundReason,
        recoveryReason: formData.recoveryReason,
      },
    });
    if (result.success) { showToast("Modification saved successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to save modification", "error");
  };

  const firstLeg = legs[0] || {};
  const lastLeg = legs[legs.length - 1] || {};
  const routeFrom = (firstLeg.departure || firstLeg.departureAirport || "").toUpperCase();
  const routeTo = (lastLeg.arrival || lastLeg.arrivalAirport || "").toUpperCase();
  const flightDateStr = firstLeg.date || firstLeg.departureDate ? formatDate(firstLeg.date || firstLeg.departureDate) : "";
  const flightTitle = legs.length > 0
    ? (
        <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
          <FlightRoute legs={legs} flightType={flightType} />
          {flightDateStr ? <span style={{ color: "#6b7280", fontWeight: 500 }}>• {flightDateStr}</span> : null}
        </span>
      )
    : "Flight";
  const flightSub = legs.length > 1 ? `${FLIGHT_TYPE_LABELS[flightType] || flightType} · ${legs.length} segments` : (firstLeg.direction || "");

  return (
    <div className={`${styles.productCard} ${styles.flightCard} ${styles.uCardFlight}`}>
      {/* Header */}
      <div className={styles.uHead}>
        <div className={styles.uHeadL}>
          <div className={`${styles.uIcon} ${styles.uIconFlight}`}><Plane size={20} /></div>
          <span className={styles.uType}>FLIGHT</span>
          <StatusPill status={canc ? (canc.state === "Cancelled" ? "Cancelled" : "Partially Cancelled") : shownStatus} />
          <CancCaseRef canc={canc} showToast={showToast} />
        </div>
        <div className={styles.uHeadR}>
          {!isLocked && <IconActionBtn icon={<Pencil size={16} />} title="Modify Flight Details" onClick={() => onEdit(component)} />}
          {!isLocked && (
            <div className={styles.actionGroup} ref={actionsRef}>
              <button className={styles.uActBtn} onClick={() => setActionsOpen(!actionsOpen)} title="More actions">
                <MoreVertical size={16} />
              </button>
              {actionsOpen && (
                <div className={styles.actionDropdown}>
                  <div className={styles.dropdownSection}>
                    <div className={styles.dropdownSectionTitle}>Standard Actions</div>
                    <button onClick={() => { onEdit(component); setActionsOpen(false); }}>
                      <Pencil size={13} /> Modify Flight Details
                    </button>
                    <button onClick={() => { setSyncOpen(true); setActionsOpen(false); }}>
                      <RefreshCw size={13} /> Sync Flight Status
                    </button>
                    {/* §9.1 — booking-level manual schedule change. The modal was
                        built and rendered but never opened: setScheduleOpen(true)
                        appeared nowhere, so this path was dead. */}
                    <button onClick={() => { setScheduleLegIdx(0); setScheduleOpen(true); setActionsOpen(false); }}>
                      <Clock size={13} /> Update Flight Schedule
                    </button>
                    <button onClick={() => { setRemoveOpen(true); setActionsOpen(false); }}>
                      <Trash2 size={13} /> Remove Flight
                    </button>
                    <button onClick={() => { setCopyOpen(true); setActionsOpen(false); }}>
                      <Copy size={13} /> Copy Flight
                    </button>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <div className={styles.dropdownSection}>
                    <div className={styles.dropdownSectionTitle}>Post-Booking Actions</div>
                    <button onClick={() => openPostBooking("Date Change")}><Clock size={13} /> Date Change</button>
                    <button onClick={() => openPostBooking("Route Change")}><MapPin size={13} /> Route Change</button>
                    <button onClick={() => openPostBooking("Flight Change / Rebooking")}><RefreshCw size={13} /> Flight Change / Rebooking</button>
                    <button onClick={() => openPostBooking("No-Show Handling")}><AlertTriangle size={13} /> No-Show Handling</button>
                    <button onClick={() => openPostBooking("Refund Request")}><RotateCcw size={13} /> Refund Request</button>
                    <button onClick={() => openPostBooking("Airline Cost Recovery")}><DollarSign size={13} /> Airline Cost Recovery</button>
                    <button onClick={() => { onOpenSSR && onOpenSSR(component); setActionsOpen(false); }}>
                      <FileCheck size={13} /> Add / Manage SSR
                    </button>
                    {/* <button onClick={() => openPostBooking("Other Modification")}><Pencil size={13} /> Other Modifications</button> */}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary + Stat strip (with cancellation summary aside when applicable) */}
      <CancTop canc={canc}>
        <SummaryBar
          title={flightTitle}
          subtitle={flightSub}
          supplier={component.supplierName || component.supplier}
          supplierRef={raw.supplierReference}
          showToast={showToast}
        />
        <StatStrip items={[
          { icon: <Users size={16} />, label: "Travelers", value: travelerStatValue(canc, tc) },
          { icon: <Tag size={16} />, label: "Type", value: FLIGHT_TYPE_LABELS[flightType] || flightType },
          { icon: <Tag size={16} />, label: "Pricing", value: PRICING_MODE_LABELS[d.flightPricingMode] || d.flightPricingMode || "—" },
          { icon: <FileText size={16} />, label: "Context", value: ctx || "—" },
          { icon: <Percent size={16} />, label: "VAT Role", value: raw.supplierCommercialRole || "—" },
        ]} />
      </CancTop>

      {/* Flight Segments — Unified Route Display */}
      <div className={styles.flightSegments}>
        {legs.length === 0 && (
          <div className={styles.emptySegments}>
            <Plane size={20} />
            <span>No flight segments added yet</span>
          </div>
        )}
        {returnBeforeOutbound(legs) && (
          <div style={{ margin: "0 0 10px", padding: "8px 12px", borderRadius: 8, background: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412", fontSize: 13 }}>
            The return flight departs before the outbound flight. Please check the dates (a wrong year?) and save the schedule again: legs are then shown outbound first.
          </div>
        )}
        {legs.length > 0 && (
          <div className={legs.length > 1 ? styles.segGroupCard : styles.segCard}>
            {legs.map((leg, i) => {
              const depCode = (leg.departure || leg.departureAirport || "").toUpperCase();
              const arrCode = (leg.arrival || leg.arrivalAirport || "").toUpperCase();
              const depAirportName = airportMap[depCode] || leg.departureName || leg.departureAirportName || "";
              const arrAirportName = airportMap[arrCode] || leg.arrivalName || leg.arrivalAirportName || "";
              const depTerminal = leg.departureTerminal || "";
              const arrTerminal = leg.arrivalTerminal || "";
              const depDate = leg.date || leg.departureDate || "";
              const depTime = leg.time || leg.departureTime || "";
              const arrDate = leg.arrivalDate || depDate;
              const arrTime = leg.arrivalTime || "";
              const fCode = `${leg.airlineCode || ""}${leg.flightNumber || ""}`;
              const airlineName = leg.airlineName || "";
              // A split round-trip books each direction on its own PNR, so the
              // code lives on the direction (outboundPnr / inboundPnr) and never
              // on the leg — reading only leg.pnr/airlinePnr left every such leg
              // with no PNR at all. Resolved the same way in the Pricing tab.
              // Trimmed because a pasted code can carry a leading space, and this
              // value is what the copy button puts on the clipboard.
              const dirPnr = String(leg.direction || "").toLowerCase() === "inbound"
                ? d.inboundPnr
                : d.outboundPnr;
              const pnr = (leg.pnr || dirPnr || d.airlinePnr || "").trim().toUpperCase();
              const cabin = leg.cabinClass || "";
              const fareLabel = leg.fareClassName || leg.fareClass || "";
              const legDirection = leg.direction || "";
              const isRT = flightType === "RT";
              // A fully-cancelled flight (or the cancelled direction of a partial
              // cancellation) forces its legs to read "Cancelled". The backend leaves
              // each leg's own flightStatus untouched on a booking-level cancel, so
              // without this override the legs keep showing their pre-cancel status.
              const flightFullyCancelled = component.status === "Cancelled" || canc?.state === "Cancelled";
              const dirCancelled = (canc?.cancelledDirections || []).some(
                (dc) => String(dc).toLowerCase() === String(legDirection).toLowerCase()
              );
              const status = (flightFullyCancelled || dirCancelled)
                ? "Cancelled"
                : (leg.flightStatus || "Confirmed");
              const isLegCancelled = status === "Cancelled" || leg.legStatus === "Cancelled";
              // A one-way flight reads exactly like one direction of a return, so it
              // gets the same header: direction tag on the left, PNR / fare / baggage /
              // status on the right. It used to put those in a footer strip under the
              // route instead, which made the two cards look like different products.
              const legLabel = legDirection
                || (isRT ? (i === 0 ? "Outbound" : "Return") : (legs.length > 1 ? `Segment ${i + 1}` : "Outbound"));
              const duration = calcDuration(depTime, arrTime);
              const stops = leg.stops || 0;
              const isReturn = legDirection === "Inbound" || (isRT && i > 0);

              return (
                <div key={leg.id || i} className={legs.length > 1 ? styles.segGroupLeg : undefined} style={isLegCancelled ? { opacity: 0.55 } : undefined}>
                  {/* Divider between legs in a grouped card */}
                  {i > 0 && legs.length > 1 && <div className={styles.segGroupDivider} />}

                  {/* Leg direction label */}
                  {legLabel && (
                    <div className={styles.segLegLabel}>
                      <span className={`${styles.segLegTag} ${isReturn ? styles.segLegRet : styles.segLegOut}`}>{legLabel}</span>
                      <div className={styles.segLegLine} />
                      <div className={styles.segLegChips}>
                        {pnr && <CopyablePnrChip pnr={pnr} showToast={showToast} />}
                        <div className={styles.segChip}><span className={styles.segChipLabel}>Fare</span><span className={styles.segChipValue}>{fareLabel || "—"}</span></div>
                        {leg.baggage?.checked?.kg ? <div className={styles.segChip}><span className={styles.segChipLabel}>Baggage</span><span className={styles.segChipValue}>{leg.baggage.checked.kg}kg</span></div> : null}
                        <div className={`${styles.segStatusChip} ${styles[`segSt${status}`]}`}>
                          <span className={styles.segStatusDot} />
                          {status}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={styles.segRouteRow}>
                    <div className={styles.segEndpoint}>
                      <div className={styles.segTopRow}>
                        <span className={styles.segTimeLarge}>{formatTime(depTime)}</span>
                        <span className={styles.segIata}>{depCode || "—"}</span>
                      </div>
                      <div className={styles.segAirportName} title={depAirportName}>{depAirportName || depCode}</div>
                      <div className={styles.segDateText}>{formatDateShort(depDate)}{depTerminal ? ` · Terminal ${depTerminal}` : ""}</div>
                    </div>

                    <div className={styles.segRouteLine}>
                      <div className={styles.segFlightMeta}>
                        {airlineName && <span className={styles.segAirlineLabel}>{airlineName}</span>}
                        <span className={styles.segFlightCode}>{fCode || "—"}</span>
                        {cabin && <span className={styles.segCabinBadge}>{cabin}</span>}
                      </div>
                      <div className={styles.segLineTrack}>
                        <div className={styles.segDotStart} />
                        <div className={styles.segDash} />
                        <div className={styles.segPlaneWrapper}>
                          <Plane size={13} className={styles.segPlaneIcon} />
                        </div>
                        <div className={styles.segDash} />
                        <div className={styles.segDotEnd} />
                      </div>
                      <div className={styles.segRouteInfo}>
                        {duration && <span className={styles.segDuration}><Clock size={10} /> {duration}</span>}
                        <span className={styles.segStopsLabel}>{stops === 0 ? "Direct" : `${stops} stop${stops > 1 ? "s" : ""}`}</span>
                      </div>
                    </div>

                    <div className={`${styles.segEndpoint} ${styles.segEndpointRight}`}>
                      <div className={styles.segTopRow}>
                        <span className={styles.segTimeLarge}>{formatTime(arrTime)}</span>
                        <span className={styles.segIata}>{arrCode || "—"}</span>
                      </div>
                      <div className={styles.segAirportName} title={arrAirportName}>{arrAirportName || arrCode}</div>
                      <div className={styles.segDateText}>{formatDateShort(arrDate)}{arrTerminal ? ` · Terminal ${arrTerminal}` : ""}</div>
                    </div>
                  </div>

                  {/* No single-leg footer: the header above now carries the PNR, fare,
                      baggage and status for every flight type, so a footer here would
                      only repeat them. */}
                </div>
              );
            })}
          </div>
        )}
        {missingReturn && (
          <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 10, border: "1px dashed #f59e0b", background: "#fffbeb", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <span className={`${styles.segLegTag} ${styles.segLegRet}`}>Inbound</span>
            <span style={{ fontWeight: 700, color: "#111827" }}>{missingReturn.from || "—"} → {missingReturn.to || "—"}</span>
            <span style={{ color: "#374151" }}>{formatDateShort(missingReturn.date)}</span>
            <span style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: "#92400e" }}>
              Return flight requested by the customer (round trip) but not booked with the airline. Book it by hand or cancel the trip.
            </span>
            <StatusPill status="Not booked" />
          </div>
        )}
      </div>

      {/* §42 — real recorded schedule changes for this flight. Renders nothing
          when there are none, so it stays out of the way until one exists. */}
      <ScheduleChangeHistory changes={scheduleChanges} />

      {/* Cancellation notice */}
      <CancNotice canc={canc} />

      {/* Footer — PNR, baggage and fare are already stated on the segments
          above, so the chips only repeated them. Just the way in. */}
      <FooterBar onView={() => onEdit(component)} />

      {/* Modals */}
      <RemoveModal open={removeOpen} onClose={() => setRemoveOpen(false)} component={component} onConfirm={() => { setRemoveOpen(false); handleRemoveConfirm(); }} />
      <CopyFlightModal open={copyOpen} onClose={() => setCopyOpen(false)} component={component} onConfirm={handleCopy} />
      <UpdateScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} component={component} onConfirm={handleScheduleUpdate} airportMap={airportMap} legIndex={scheduleLegIdx} />
      {/* §21 — the check is real now; when the supplier cannot be reached it
          hands the operator over to the manual path rather than guessing. */}
      <SyncFlightModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        component={component}
        onManualChange={() => setScheduleOpen(true)}
      />
      <PostBookingModal open={postBookingOpen} onClose={() => setPostBookingOpen(false)} component={component} actionType={postBookingAction} onConfirm={handlePostBooking} airportMap={airportMap} />
    </div>
  );
};

/* ══════════════════════════════════════════════
   HOTEL CARD
   ══════════════════════════════════════════════ */
const CopyProductModal = ({ open, onClose, component, productLabel, onConfirm }) => {
  const [supplierRef, setSupplierRef] = useState("");

  if (!open) return null;
  return (
    <BaseModal open={open} onClose={onClose} title={`Copy ${productLabel}`} width="420px">
      <div className={styles.modalBody}>
        <div className={styles.formGroup}>
          <label>Supplier Reference Number <span className={styles.required}>*</span></label>
          <input
            type="text"
            value={supplierRef}
            onChange={(e) => setSupplierRef(e.target.value)}
            placeholder="Enter supplier reference"
          />
        </div>
        <div className={styles.modalActions}>
          <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button className={styles.btnPrimary} onClick={() => { onConfirm({ supplierRef }); setSupplierRef(""); onClose(); }} disabled={!supplierRef}>
            <Copy size={13} /> Copy {productLabel}
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

const HotelCard = ({ component, isLocked, onEdit, onDelete, bookingId, onRefresh, airportMap = {}, canc }) => {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [postBookingOpen, setPostBookingOpen] = useState(false);
  const [postBookingAction, setPostBookingAction] = useState("");
  const actionsRef = useRef(null);

  const { showToast } = useToast();
  const copyApi = useCopyProduct();
  const deleteApi = useDeleteBookingProduct();
  const modificationApi = useCreateProductModification();

  const closeActions = useCallback(() => setActionsOpen(false), []);
  useClickOutside(actionsRef, closeActions);

  const raw = component._raw || {};
  const d = raw.productDetails || {};
  const rooms = d.rooms && d.rooms.length > 0 ? d.rooms : [{
    roomName: d.roomName || "", roomType: d.roomType || "",
    roomCharacteristic: d.roomCharacteristic || "",
    boardCode: d.boardCode || "", boardName: d.boardName || "",
    numberOfRooms: d.numberOfRooms || 1,
  }];
  // Total room count = at least the number of distinct room entries, but honour an
  // explicit numberOfRooms when it is higher (e.g. multiple of the same room type).
  const roomCount = Math.max(
    Number(d.numberOfRooms) || 0,
    rooms.reduce((sum, r) => sum + (Number(r.numberOfRooms) || 1), 0)
  ) || 1;
  const hasMultipleRooms = rooms.length > 1;
  const tc = component.travelersCount || 0;

  const openPostBooking = (action) => {
    setPostBookingAction(action);
    setPostBookingOpen(true);
    setActionsOpen(false);
  };

  const handleCopy = async (formData) => {
    if (!bookingId) return;
    const result = await copyApi.execute(bookingId, component.id, { supplierReference: formData.supplierRef });
    if (result.success) { showToast("Accommodation copied successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to copy accommodation", "error");
  };

  const handleRemoveConfirm = async () => {
    if (!bookingId) return;
    const result = await deleteApi.execute(bookingId, component.id);
    if (result.success) { showToast("Accommodation removed successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to remove accommodation", "error");
  };

  const handlePostBooking = async (formData) => {
    if (!bookingId) return;
    const result = await modificationApi.execute(bookingId, component.id, {
      modificationType: formData.modificationType || "OTHER",
      modificationTitle: formData.modificationTitle || postBookingAction,
      internalNotes: formData.internalNotes,
      supplierCost: formData.supplierCost,
      agencyFee: formData.agencyFee,
      refundAmount: formData.refundAmount,
      regenerateDocuments: formData.regenerateDocuments,
      notifyCustomer: formData.notifyCustomer,
      changeData: {
        legEdits: formData.legEdits,
        // Hotel dates
        newCheckIn: formData.newCheckIn,
        newCheckOut: formData.newCheckOut,
        newNights: formData.newNights,
        // Hotel room details
        newRoomName: formData.newRoomName,
        newRoomType: formData.newRoomType,
        newRoomCharacteristic: formData.newRoomCharacteristic,
        newRoomStatus: formData.newRoomStatus,
        newRoomRate: formData.newRoomRate,
        newNumberOfRooms: formData.newNumberOfRooms,
        newBoardCode: formData.newBoardCode,
        newBoardName: formData.newBoardName,
        newRoom: formData.newRoom,
        newBoard: formData.newBoard,
        extraNights: formData.extraNights,
        noShowDate: formData.noShowDate,
        refundReason: formData.refundReason,
      },
    });
    if (result.success) { showToast("Modification saved successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to save modification", "error");
  };

  return (
    <div className={`${styles.productCard} ${styles.uCardHotel}`}>
      {/* Header */}
      <div className={styles.uHead}>
        <div className={styles.uHeadL}>
          <div className={`${styles.uIcon} ${styles.uIconHotel}`}><Hotel size={20} /></div>
          <span className={styles.uType}>HOTEL</span>
          <StatusPill status={canc ? (canc.state === "Cancelled" ? "Cancelled" : "Partially Cancelled") : component.status} />
          <CancCaseRef canc={canc} showToast={showToast} />
        </div>
        <div className={styles.uHeadR}>
          {!isLocked && <IconActionBtn icon={<Pencil size={16} />} title="Modify Accommodation Details" onClick={() => onEdit(component)} />}
          {!isLocked && (
            <div className={styles.actionGroup} ref={actionsRef}>
              <button className={styles.uActBtn} onClick={() => setActionsOpen(!actionsOpen)} title="More actions">
                <MoreVertical size={16} />
              </button>
              {actionsOpen && (
                <div className={styles.actionDropdown}>
                  <div className={styles.dropdownSection}>
                    <div className={styles.dropdownSectionTitle}>Standard Actions</div>
                    <button onClick={() => { onEdit(component); setActionsOpen(false); }}>
                      <Pencil size={13} /> Modify Accommodation Details
                    </button>
                    <button onClick={() => { setRemoveOpen(true); setActionsOpen(false); }}>
                      <Trash2 size={13} /> Remove Accommodation
                    </button>
                    <button onClick={() => { setCopyOpen(true); setActionsOpen(false); }}><Copy size={13} /> Copy Accommodation</button>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <div className={styles.dropdownSection}>
                    <div className={styles.dropdownSectionTitle}>Post-Booking Actions</div>
                    <button onClick={() => openPostBooking("Date Modification")}><Clock size={13} /> Date Modification</button>
                    <button onClick={() => openPostBooking("Room Change")}><ArrowRight size={13} /> Room Change</button>
                    <button onClick={() => openPostBooking("Board Type Change")}><ArrowRight size={13} /> Board Type Change</button>
                    <button onClick={() => openPostBooking("Add Extra Night")}><Plus size={13} /> Add Extra Night</button>
                    <button onClick={() => openPostBooking("No-Show Handling")}><AlertTriangle size={13} /> No-Show Handling</button>
                    {/* <button onClick={() => openPostBooking("Other Modification")}><Pencil size={13} /> Other Modifications</button> */}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary + Stat strip (with cancellation summary aside when applicable) */}
      <CancTop canc={canc}>
        <SummaryBar
          title={`${d.hotelName || "Hotel"}${d.hotelStars ? `  ${"★".repeat(Math.min(5, Math.round(d.hotelStars)))}` : ""}`}
          subtitle={d.checkIn && d.checkOut ? `${formatDate(d.checkIn)} → ${formatDate(d.checkOut)}${d.nights ? ` (${d.nights} night${d.nights !== 1 ? "s" : ""})` : ""}` : ([d.hotelCity, d.hotelCountry].filter(Boolean).join(", "))}
          supplier={component.supplierName || component.supplier}
          supplierRef={raw.supplierReference}
          showToast={showToast}
        />
        <StatStrip items={[
          { icon: <Users size={16} />, label: "Travelers", value: travelerStatValue(canc, tc) },
          { icon: <Bed size={16} />, label: "Rooms", value: `${roomCount} room${roomCount !== 1 ? "s" : ""}` },
          { icon: <Tag size={16} />, label: "Board", value: hasMultipleRooms ? "See rooms below" : (d.boardName || d.boardCode || rooms[0]?.boardName || rooms[0]?.boardCode || "—") },
          { icon: <Bed size={16} />, label: "Room Type", value: hasMultipleRooms ? "Multiple rooms" : (rooms[0]?.roomName || rooms[0]?.roomType || "—") },
          { icon: <Globe size={16} />, label: "Room View", value: hasMultipleRooms ? "See rooms below" : (rooms[0]?.roomCharacteristic || "—") },
          { icon: <Percent size={16} />, label: "VAT Role", value: raw.supplierCommercialRole || "Merchant" },
        ]} />
      </CancTop>

      {/* Check-in / Check-out box */}
      <div className={styles.uBox}>
        <div className={styles.uBoxGrid}>
          <div className={styles.uBoxCell}>
            <span className={styles.uStatIcon}><Calendar size={16} /></span>
            <span className={styles.uStatTxt}>
              <span className={styles.uStatLabel}>Check-in</span>
              <span className={styles.uStatValue}>{formatDate(d.checkIn)}</span>
            </span>
          </div>
          <div className={styles.uBoxCell}>
            <span className={styles.uStatIcon}><Calendar size={16} /></span>
            <span className={styles.uStatTxt}>
              <span className={styles.uStatLabel}>Check-out</span>
              <span className={styles.uStatValue}>{formatDate(d.checkOut)}</span>
            </span>
          </div>
          <div className={styles.uBoxCell}>
            <span className={styles.uStatIcon}><Moon size={16} /></span>
            <span className={styles.uStatTxt}>
              <span className={styles.uStatLabel}>Nights</span>
              <span className={styles.uStatValue}>{d.nights ? `${d.nights} night${d.nights !== 1 ? "s" : ""}` : "—"}</span>
            </span>
          </div>
          <div className={styles.uBoxCell}>
            <span className={styles.uStatIcon}><Building2 size={16} /></span>
            <span className={styles.uStatTxt}>
              <span className={styles.uStatLabel}>City / Country</span>
              <span className={styles.uStatValue}>{[d.hotelCity, d.hotelCountry].filter(Boolean).join(", ") || d.hotelAddress || "—"}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Rooms list (always show every room when more than one) */}
      {!canc && hasMultipleRooms && (
        <PartsBreakdown
          title={`Rooms (${rooms.length})`}
          items={rooms.map((r, i) => ({
            label: `${r.roomName || r.roomType || `Room ${i + 1}`}${(Number(r.numberOfRooms) || 1) > 1 ? ` ×${r.numberOfRooms}` : ""}`,
            sub: [r.boardName || r.boardCode, r.roomCharacteristic].filter(Boolean).join(" · "),
            status: (r.status || r.roomStatus) === "Cancelled" ? "Cancelled" : "Active",
          }))}
        />
      )}

      {/* Rooms breakdown (which room is cancelled) */}
      {canc && <PartsBreakdown title={canc.partsTitle} items={canc.parts} />}

      {/* Cancellation notice */}
      <CancNotice canc={canc} />

      {/* Footer */}
      <FooterBar
        onView={() => onEdit(component)}
        chips={[
          { label: "Room", value: hasMultipleRooms ? `${rooms.length} rooms` : (rooms[0]?.roomName || rooms[0]?.roomType) },
          { label: "Cancellation", value: rooms[0]?.roomRate },
          { label: "Supplier", value: component.supplierName || component.supplier },
        ]}
      />

      {/* Modals */}
      <RemoveModal open={removeOpen} onClose={() => setRemoveOpen(false)} component={component} onConfirm={() => { setRemoveOpen(false); handleRemoveConfirm(); }} />
      <CopyProductModal open={copyOpen} onClose={() => setCopyOpen(false)} component={component} productLabel="Accommodation" onConfirm={handleCopy} />
      <PostBookingModal open={postBookingOpen} onClose={() => setPostBookingOpen(false)} component={component} actionType={postBookingAction} onConfirm={handlePostBooking} airportMap={airportMap} />
    </div>
  );
};

/* ══════════════════════════════════════════════
   TRANSFER CARD
   ══════════════════════════════════════════════ */
const TransferCard = ({ component, isLocked, onEdit, onDelete, bookingId, onRefresh, airportMap = {}, canc }) => {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [postBookingOpen, setPostBookingOpen] = useState(false);
  const [postBookingAction, setPostBookingAction] = useState("");
  const actionsRef = useRef(null);

  const { showToast } = useToast();
  const copyApi = useCopyProduct();
  const deleteApi = useDeleteBookingProduct();
  const modificationApi = useCreateProductModification();

  const closeActions = useCallback(() => setActionsOpen(false), []);
  useClickOutside(actionsRef, closeActions);

  const raw = component._raw || {};
  const d = raw.productDetails || {};
  const sched = d.schedule || {};
  const fl = d.flightLink || {};
  const ctx = raw.booking_context || raw.bookingContext || "";
  const tc = component.travelersCount || 0;

  const openPostBooking = (action) => {
    setPostBookingAction(action);
    setPostBookingOpen(true);
    setActionsOpen(false);
  };

  const handleCopy = async (formData) => {
    if (!bookingId) return;
    const result = await copyApi.execute(bookingId, component.id, { supplierReference: formData.supplierRef });
    if (result.success) { showToast("Transfer copied successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to copy transfer", "error");
  };

  const handleRemoveConfirm = async () => {
    if (!bookingId) return;
    const result = await deleteApi.execute(bookingId, component.id);
    if (result.success) { showToast("Transfer removed successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to remove transfer", "error");
  };

  const handlePostBooking = async (formData) => {
    if (!bookingId) return;
    const result = await modificationApi.execute(bookingId, component.id, {
      modificationType: formData.modificationType || "OTHER",
      modificationTitle: formData.modificationTitle || postBookingAction,
      internalNotes: formData.internalNotes,
      supplierCost: formData.supplierCost,
      agencyFee: formData.agencyFee,
      refundAmount: formData.refundAmount,
      regenerateDocuments: formData.regenerateDocuments,
      notifyCustomer: formData.notifyCustomer,
      changeData: {
        legEdits: formData.legEdits,
        newCount: formData.newCount,
        noShowDate: formData.noShowDate,
        refundReason: formData.refundReason,
      },
    });
    if (result.success) { showToast("Modification saved successfully", "success"); onRefresh?.(); }
    else showToast(result.message || "Failed to save modification", "error");
  };

  return (
    <div className={`${styles.productCard} ${styles.uCardTransfer}`}>
      {/* Header */}
      <div className={styles.uHead}>
        <div className={styles.uHeadL}>
          <div className={`${styles.uIcon} ${styles.uIconTransfer}`}><Bus size={20} /></div>
          <span className={styles.uType}>TRANSFER</span>
          <StatusPill status={canc ? (canc.state === "Cancelled" ? "Cancelled" : "Partially Cancelled") : component.status} />
          <CancCaseRef canc={canc} showToast={showToast} />
        </div>
        <div className={styles.uHeadR}>
          {!isLocked && <IconActionBtn icon={<Pencil size={16} />} title="Modify Transfer" onClick={() => onEdit(component)} />}
          {!isLocked && (
            <div className={styles.actionGroup} ref={actionsRef}>
              <button className={styles.uActBtn} onClick={() => setActionsOpen(!actionsOpen)} title="More actions">
                <MoreVertical size={16} />
              </button>
              {actionsOpen && (
                <div className={styles.actionDropdown}>
                  <div className={styles.dropdownSection}>
                    <div className={styles.dropdownSectionTitle}>Standard Actions</div>
                    <button onClick={() => { onEdit(component); setActionsOpen(false); }}>
                      <Pencil size={13} /> Modify Transfer
                    </button>
                    <button onClick={() => { setRemoveOpen(true); setActionsOpen(false); }}>
                      <Trash2 size={13} /> Remove Transfer
                    </button>
                    <button onClick={() => { setCopyOpen(true); setActionsOpen(false); }}><Copy size={13} /> Copy Transfer</button>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <div className={styles.dropdownSection}>
                    <div className={styles.dropdownSectionTitle}>Post-Booking Actions</div>
                    <button onClick={() => openPostBooking("Date Change")}><Clock size={13} /> Date Change</button>
                    <button onClick={() => openPostBooking("Route Change")}><MapPin size={13} /> Route Change</button>
                    <button onClick={() => openPostBooking("Transfer Rebook")}><RefreshCw size={13} /> Transfer Rebook</button>
                    <button onClick={() => openPostBooking("Traveler Count Change")}><Users size={13} /> Traveler Count Change</button>
                    <button onClick={() => openPostBooking("No-Show Handling")}><AlertTriangle size={13} /> No-Show Handling</button>
                    <button onClick={() => openPostBooking("Refund Request")}><RotateCcw size={13} /> Refund Request</button>
                    {/* <button onClick={() => openPostBooking("Other Modification")}><Pencil size={13} /> Other Modifications</button> */}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary + Stat strip (with cancellation summary aside when applicable) */}
      <CancTop canc={canc}>
        <SummaryBar
          title={(() => {
            const s = d.transferMoment === "RoundTrip" ? (d.arrivalLeg?.schedule || {}) : sched;
            return s.pickupLocation && s.dropoffLocation ? `${s.pickupLocation} → ${s.dropoffLocation}` : "Transfer";
          })()}
          subtitle={[d.transferMoment, d.transferType].filter(Boolean).length ? `${[d.transferMoment, d.transferType].filter(Boolean).join(" · ")} Transfer` : ctx}
          supplier={component.supplierName || component.supplier}
          supplierRef={raw.supplierReference}
          showToast={showToast}
        />
        <StatStrip items={[
          { icon: <Users size={16} />, label: "Travelers", value: travelerStatValue(canc, tc) },
          { icon: <Tag size={16} />, label: "Transfer Type", value: d.transferType || "—" },
          { icon: <Calendar size={16} />, label: "Date", value: formatDate(d.transferMoment === "RoundTrip" ? d.arrivalLeg?.schedule?.pickupDate : sched.pickupDate) },
          { icon: <Clock size={16} />, label: "Time", value: formatTime(d.transferMoment === "RoundTrip" ? d.arrivalLeg?.schedule?.pickupTime : sched.pickupTime) },
          { icon: <Car size={16} />, label: "Vehicle Type", value: d.vehicleType ? `Private ${d.vehicleType}` : "—" },
          { icon: <Percent size={16} />, label: "VAT Role", value: raw.supplierCommercialRole || "Agent" },
        ]} />
      </CancTop>

      {/* From → To route box(es) */}
      {(d.transferMoment === "RoundTrip"
        ? [{ tag: "Arrival", s: d.arrivalLeg?.schedule || {}, fl: d.arrivalLeg?.flightLink || {} }, { tag: "Departure", s: d.departureLeg?.schedule || {}, fl: d.departureLeg?.flightLink || {} }]
        : [{ tag: d.transferMoment || "Transfer", s: sched, fl }]
      ).map((leg, i) => (
        <div key={i} className={styles.uBox}>
          <div className={styles.uRoute}>
            <div className={styles.uRouteEnd}>
              <span className={styles.uRouteTag}>From</span>
              <div className={styles.uRouteName}>{leg.s.pickupLocation || "—"}</div>
              <div className={styles.uRouteSub}>
                {formatDate(leg.s.pickupDate)} · {leg.s.pickupTimeType === "tba" ? "To Be Advised" : leg.s.pickupTimeType === "voucher" ? "Follow Voucher" : formatTime(leg.s.pickupTime)}
              </div>
            </div>
            <div className={styles.uRouteLine}>
              <span className={styles.uRouteDot} />
              <span className={styles.uRouteDash} />
              <span className={styles.uRouteArrow}><ArrowRight size={16} /></span>
              <span className={styles.uRouteDash} />
              <span className={styles.uRouteDot} />
            </div>
            <div className={`${styles.uRouteEnd} ${styles.uRouteEndRight}`}>
              <span className={styles.uRouteTag}>To</span>
              <div className={styles.uRouteName}>{leg.s.dropoffLocation || "—"}</div>
              {(leg.fl?.flightNumber || leg.fl?.airlineCode) ? (
                <div className={styles.uRouteSub}>Flight {leg.fl.airlineCode || ""}{leg.fl.flightNumber || ""} · {leg.fl.time || "—"}</div>
              ) : null}
            </div>
          </div>
        </div>
      ))}

      {/* Instructions & Contact Details */}
      {(d.pickupInstructions || d.cantFindInstructions || d.travelDisruptionInfo || d.contactInfoSupplier || d.emergencyAssistance ||
        d.arrivalLeg?.pickupInstructions || d.arrivalLeg?.cantFindInstructions ||
        d.departureLeg?.pickupInstructions || d.departureLeg?.cantFindInstructions) && (
        <div className={styles.detailSection}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "12px 16px" }}>
            {/* For RoundTrip, show per-leg instructions */}
            {d.transferMoment === "RoundTrip" ? (
              <>
                {(d.arrivalLeg?.pickupInstructions || d.arrivalLeg?.cantFindInstructions) && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 8, textTransform: "uppercase" }}>Arrival Instructions</div>
                    {d.arrivalLeg.pickupInstructions && (
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Pickup Instructions</span>
                        <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.arrivalLeg.pickupInstructions}</p>
                      </div>
                    )}
                    {d.arrivalLeg.cantFindInstructions && (
                      <div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Can't Find Transfer</span>
                        <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.arrivalLeg.cantFindInstructions}</p>
                      </div>
                    )}
                  </div>
                )}
                {(d.departureLeg?.pickupInstructions || d.departureLeg?.cantFindInstructions) && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 8, textTransform: "uppercase" }}>Departure Instructions</div>
                    {d.departureLeg.pickupInstructions && (
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Pickup Instructions</span>
                        <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.departureLeg.pickupInstructions}</p>
                      </div>
                    )}
                    {d.departureLeg.cantFindInstructions && (
                      <div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Can't Find Transfer</span>
                        <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.departureLeg.cantFindInstructions}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {d.pickupInstructions && (
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Pickup Instructions</span>
                    <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.pickupInstructions}</p>
                  </div>
                )}
                {d.cantFindInstructions && (
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Can't Find Transfer</span>
                    <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.cantFindInstructions}</p>
                  </div>
                )}
              </>
            )}
            {/* Shared operational fields — shown for all transfer types */}
            {d.travelDisruptionInfo && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Travel Disruption Info</span>
                <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.travelDisruptionInfo}</p>
              </div>
            )}
            {d.contactInfoSupplier && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Contact Info — Transfer Supplier</span>
                <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.contactInfoSupplier}</p>
              </div>
            )}
            {d.emergencyAssistance && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Emergency Assistance</span>
                <p style={{ margin: "2px 0 0", fontSize: 13, color: "#1f2937" }}>{d.emergencyAssistance}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transfer breakdown (which leg is cancelled) */}
      {canc && <PartsBreakdown title={canc.partsTitle} items={canc.parts} />}

      {/* Cancellation notice */}
      <CancNotice canc={canc} />

      {/* Footer */}
      <FooterBar
        onView={() => onEdit(component)}
        chips={[
          { label: "Transfer Context", value: ctx },
          { label: "Vehicle", value: d.vehicleType ? `Private ${d.vehicleType}` : "" },
          { label: "Supplier", value: component.supplierName || component.supplier },
        ]}
      />

      {/* Modals */}
      <RemoveModal open={removeOpen} onClose={() => setRemoveOpen(false)} component={component} onConfirm={() => { setRemoveOpen(false); handleRemoveConfirm(); }} />
      <CopyProductModal open={copyOpen} onClose={() => setCopyOpen(false)} component={component} productLabel="Transfer" onConfirm={handleCopy} />
      <PostBookingModal open={postBookingOpen} onClose={() => setPostBookingOpen(false)} component={component} actionType={postBookingAction} onConfirm={handlePostBooking} airportMap={airportMap} />
    </div>
  );
};

/* ══════════════════════════════════════════════
   GENERIC FALLBACK CARD
   ══════════════════════════════════════════════ */
const SIMPLE_TYPE_LABEL = {
  CarRental: "Car Rental", Insurance: "Insurance",
  Package: "External Package", Visa: "Visa", Cruise: "Cruise",
};

/* Extra baggage sold online, per traveler and flight, with its state at the airline:
   orange "not yet booked" (staff add it with the airline, then set the product to Confirmed)
   or green "confirmed". Levent's design, 5 Nov 2026. */
const fmtNlDate = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(String(iso || "")) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const BaggagePanel = ({ lines = [], confirmed, travelers = [], airportMap = {}, flightPnr = "" }) => {
  const people = [...travelers].sort((a, b) => Number(a.id) - Number(b.id));
  const nameOf = (i) => people[i]?.name || `Reiziger ${i + 1}`;
  const place = (code) => (code ? `${(airportMap[code] || "").split(",")[0] || code} (${code})` : "—");
  // One row per traveler and direction, with that traveler's bags on that flight.
  const rows = [];
  for (const l of lines) {
    const key = `${Number(l.traveller) || 0}|${l.direction === "ret" ? "ret" : "out"}`;
    let r = rows.find((x) => x.key === key);
    if (!r) { r = { key, traveller: Number(l.traveller) || 0, info: l.routeInfo || null, bags: [] }; rows.push(r); }
    r.bags.push(l);
  }
  const bagLabel = (l) => (l.code === "baggage.cabin" ? `Handbagage${l.kg ? ` ${l.kg} kg` : ""}` : `Ruimbagage${l.kg ? ` ${l.kg} kg` : ""}`);
  const tone = confirmed
    ? { border: "#86efac", bg: "#f0fdf4", title: "#15803d", text: "#166534" }
    : { border: "#fdba74", bg: "#fff7ed", title: "#c2410c", text: "#9a3412" };
  const th = { textAlign: "left", padding: "8px 10px", fontSize: 12, fontWeight: 600, color: "#6b7280", background: "#f3f4f6" };
  const td = { padding: "9px 10px", fontSize: 13, color: "#111827", borderTop: "1px solid #eef0f3" };
  return (
    <div style={{ margin: "10px 16px", padding: "14px 16px", borderRadius: 12, border: `1px solid ${tone.border}`, background: tone.bg }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ width: 30, height: 30, borderRadius: "50%", flex: "0 0 30px", display: "grid", placeItems: "center", background: confirmed ? "#16a34a" : "#f59e0b", color: "#fff" }}>
          {confirmed ? <Check size={17} /> : <AlertTriangle size={16} />}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: tone.title }}>
            {confirmed ? "Extra bagage bevestigd bij de luchtvaartmaatschappij" : "Extra bagage – Nog niet geboekt bij de luchtvaartmaatschappij"}
          </div>
          <div style={{ fontSize: 13, color: tone.text, marginTop: 2 }}>
            {confirmed ? "De bestelde extra bagage is succesvol toegevoegd aan de vluchtboeking." : "De klant heeft extra bagage betaald, maar deze is nog niet toegevoegd aan de vluchtboeking."}
          </div>
          <div style={{ overflowX: "auto", marginTop: 10, borderRadius: 8, background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
              <thead><tr>
                <th style={th}>Reiziger</th><th style={th}>Traject</th><th style={th}>Vlucht</th><th style={th}>Datum</th>
                <th style={th}>{confirmed ? "Bevestigde bagage" : "Bestelde bagage"}</th>{confirmed && <th style={th} />}
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td style={{ ...td, fontWeight: 600 }}>{nameOf(r.traveller)}</td>
                    <td style={td}>{r.info ? `${place(r.info.from)} → ${place(r.info.to)}` : "—"}</td>
                    <td style={td}>{r.info?.flight || "—"}</td>
                    <td style={td}>{fmtNlDate(r.info?.date)}</td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                        {r.bags.map((l, i) => <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Package size={14} />{bagLabel(l)}</span>)}
                      </div>
                    </td>
                    {confirmed && <td style={td}><span style={{ padding: "3px 9px", borderRadius: 999, background: "#dcfce7", color: "#15803d", fontSize: 12, fontWeight: 600 }}>✓ Bevestigd</span></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!confirmed && (
            <div style={{ fontSize: 13, color: tone.text, marginTop: 10 }}>
              <b>Actie:</b> Voeg de bovenstaande bagage toe bij de luchtvaartmaatschappij{flightPnr ? <> (PNR <b>{flightPnr}</b>)</> : null} en zet dit product daarna op Confirmed.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const GenericProductCard = ({ component, isLocked, onEdit, onDelete, canc, flightPnr = "", travelers = [], airportMap = {} }) => {
  const { showToast } = useToast();
  const CIcon = ICON_MAP[component.type] || FileText;
  const raw = component._raw || {};
  const d = raw.productDetails || {};
  const tc = component.travelersCount || 0;
  const allFields = (component.fields || []).filter((f) => f && f.value);

  const typeLabel = (SIMPLE_TYPE_LABEL[component.type] || component.type || "Product").toUpperCase();

  /* Supplier ref comes from a "…Ref" field if present, else the raw value */
  const refField = allFields.find((f) => /ref/i.test(f.label));
  const supplierRef = refField && refField.value !== "—" ? refField.value : raw.supplierReference;

  /* Non-ref fields drive the summary title + stat strip */
  const infoFields = allFields.filter((f) => !/ref/i.test(f.label));
  const title = infoFields[0]?.value && infoFields[0].value !== "—"
    ? infoFields[0].value
    : (SIMPLE_TYPE_LABEL[component.type] || component.type);
  const subtitle = infoFields[1]?.value && infoFields[1].value !== "—"
    ? `${infoFields[1].label}: ${infoFields[1].value}`
    : "";

  const statItems = [
    { icon: <Users size={16} />, label: "Travelers", value: canc ? travelerStatValue(canc, tc) : (tc ? `${tc} traveler${tc !== 1 ? "s" : ""}` : null) },
    ...infoFields.slice(2, 6).map((f) => ({ icon: STAT_ICON_FOR_LABEL(f.label), label: f.label, value: f.value })),
    { icon: <Percent size={16} />, label: "VAT Role", value: raw.supplierCommercialRole || "Merchant" },
  ].filter((s) => s.value);

  return (
    <div className={`${styles.productCard} ${styles[CARD_ACCENT_CLASS[component.type]] || styles.uCardOther}`}>
      <CardHead
        icon={<CIcon size={20} />}
        type={typeLabel}
        accentType={component.type}
        status={canc ? (canc.state === "Cancelled" ? "Cancelled" : "Partially Cancelled") : component.status}
        canc={canc}
        showToast={showToast}
        right={!isLocked && (
          <>
            <IconActionBtn icon={<Pencil size={16} />} title="Edit" onClick={() => onEdit(component)} />
            <CardMenu items={[
              { icon: <Pencil size={13} />, label: "Edit Details", onClick: () => onEdit(component) },
              { icon: <Trash2 size={13} />, label: "Remove", onClick: () => onDelete(component), danger: true },
            ]} />
          </>
        )}
      />
      <CancTop canc={canc}>
        <SummaryBar
          title={title}
          subtitle={subtitle}
          supplier={component.supplierName || component.supplier}
          supplierRef={supplierRef}
          showToast={showToast}
        />
        <StatStrip items={statItems} />
      </CancTop>
      {canc && <PartsBreakdown title={canc.partsTitle} items={canc.parts} />}
      {d.kind === "baggage" && Array.isArray(d.lines) && d.lines.length > 0 && component.status !== "Cancelled" && (
        <BaggagePanel lines={d.lines} confirmed={component.status === "Confirmed"} travelers={travelers} airportMap={airportMap} flightPnr={flightPnr} />
      )}
      <CancNotice canc={canc} />
      <FooterBar
        onView={() => onEdit(component)}
        chips={[
          { label: "Currency", value: d.currency || d.currencyCode },
          { label: "Rate Type", value: d.rateType || d.rateClass },
          { label: "Supplier", value: component.supplierName || component.supplier },
        ]}
      />
    </div>
  );
};

/* ══════════════════════════════════════════════
   MAIN ITINERARY TAB
   ══════════════════════════════════════════════ */
/* ── Tab configuration for product types ── */
const PRODUCT_TAB_CONFIG = {
  Flight:    { label: "Flights",    icon: Plane,    cssClass: "tabFlight" },
  Hotel:     { label: "Hotels",     icon: Hotel,    cssClass: "tabHotel" },
  Transfer:  { label: "Transfers",  icon: Bus,      cssClass: "tabTransfer" },
  Insurance: { label: "Insurance",  icon: Shield,   cssClass: "tabInsurance" },
  Package:   { label: "Packages",   icon: Package,  cssClass: "tabPackage" },
  CarRental: { label: "Car Rental", icon: Car,      cssClass: "tabCarRental" },
  Visa:      { label: "Visa",       icon: FileText, cssClass: "tabVisa" },
  Cruise:    { label: "Cruises",    icon: Ship,     cssClass: "tabCruise" },
};

/* ── SSR Category SVG Icons (clean, no emojis) ── */
const SeatIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 18V6a2 2 0 0 1 2-2h4v12H4z"/><path d="M14 4h4a2 2 0 0 1 2 2v12h-6V4z"/><path d="M2 18h20v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2z"/></svg>;
const MealIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/></svg>;
const BagIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 20h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>;
const HandbagIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="8" width="18" height="13" rx="2"/><path d="M8 8V5a4 4 0 0 1 8 0v3"/></svg>;
const WheelchairIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="16" cy="18" r="3"/><path d="M4 4h3l2.5 12.5a2 2 0 0 0 2 1.5h6"/><circle cx="10" cy="5" r="2"/></svg>;
const SkiIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 20l14-6M2 22l4-4M18 8l2 2"/><circle cx="14" cy="6" r="2"/></svg>;
const PetIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 10c-1.5-2-4-3-6-2s-3 4-1 6c1.5 1.5 4 2 6 2s4.5-.5 6-2c2-2 1-5-1-6s-4.5 0-6 2z"/><circle cx="5" cy="5" r="1.5"/><circle cx="19" cy="5" r="1.5"/><circle cx="3" cy="11" r="1.5"/><circle cx="21" cy="11" r="1.5"/></svg>;
const ClipboardIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="8" y="2" width="8" height="4" rx="1"/><rect x="4" y="4" width="16" height="18" rx="2"/><path d="M12 11h4M12 16h4M8 11h.01M8 16h.01"/></svg>;

const ITINERARY_SSR_CAT_ICONS = {
  SEAT: SeatIcon, MEAL: MealIcon, BAGGAGE: BagIcon, HANDBAG: HandbagIcon,
  ASSIST: WheelchairIcon, SPORT: SkiIcon, PET: PetIcon, OTHER: ClipboardIcon,
};
const SSRCatIcon = ({ code, style }) => {
  const Icon = ITINERARY_SSR_CAT_ICONS[code] || ClipboardIcon;
  return <span style={style}><Icon /></span>;
};

/* ── SSR Modal Inline SVG Icons (same as BookingDetail TravelersTab) ── */
const SSRSearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
);
const SSRXIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
);
const SSRPlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
);
const SSRPlaneIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>
);
const SSRRouteIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>
);
const SSRUserIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
);
const SSRArrowLeftIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
);
const SSRSaveIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17,21 17,13 7,13 7,21"/><polyline points="7,3 7,8 15,8"/></svg>
);

const SSR_CAT_STYLE = SSR_CATEGORIES.reduce((acc, c) => { acc[c.code] = { color: c.color, bg: c.bg, icon: c.icon, name: c.name, description: c.description }; return acc; }, {});

const ItineraryTab = ({ components, onAdd, onEdit, onDelete, isLocked, bookingId, onRefresh, ssrs = [], travelers = [], assignments = [], setSSRPricingApi }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("__all__");

  /* ── Cancellation cases (drive the per-product cancellation engine UI) ── */
  const { execute: fetchCancellationCases } = useGetCancellationCases();
  const [cancellationCases, setCancellationCases] = useState([]);
  useEffect(() => {
    if (!bookingId) return;
    fetchCancellationCases(bookingId).then((res) => {
      if (res.success && Array.isArray(res.data)) setCancellationCases(res.data);
    });
  }, [bookingId, fetchCancellationCases]);

  /* Per-product cancellation view-model (null entries omitted → fully-active
     products render exactly as before). */
  const cancByProduct = useMemo(() => {
    const map = {};
    (components || []).forEach((c) => {
      const vm = buildCardCancellation({ component: c, cancellationCases, travelers, assignments });
      if (vm) map[c.id] = vm;
    });
    return map;
  }, [components, cancellationCases, travelers, assignments]);

  // Load airports for code → name lookup (flight cards need full airport names)
  const { data: airportsData } = useGetAirports({ limit: 20000, sortBy: "code", sortOrder: "ASC" });
  const airportMap = useMemo(() => {
    const map = {};
    (airportsData || []).forEach((a) => {
      if (a.code) map[a.code.toUpperCase()] = a.name || a.code;
    });
    return map;
  }, [airportsData]);

  /* ── Count components by type, determine which tabs to show ── */
  const countByType = useMemo(() => {
    const map = {};
    components.forEach((c) => {
      const t = c.type || "Other";
      map[t] = (map[t] || 0) + 1;
    });
    return map;
  }, [components]);

  /* Build ordered list of product types present in this booking */
  const presentTypes = useMemo(() => {
    const order = ["Flight", "Hotel", "Transfer", "Insurance", "Package", "CarRental", "Visa", "Cruise"];
    const found = order.filter((t) => countByType[t] > 0);
    // Add any types not in the predefined order
    Object.keys(countByType).forEach((t) => {
      if (!found.includes(t)) found.push(t);
    });
    return found;
  }, [countByType]);

  /* Only show tabs if there are 2+ different product types, or 2+ components */
  const showTabs = components.length > 1 && presentTypes.length >= 1;

  /* Filter components based on active tab */
  const filteredComponents = useMemo(() => {
    if (activeTab === "__all__") return components;
    return components.filter((c) => (c.type || "Other") === activeTab);
  }, [components, activeTab]);

  /* Active tab config */
  const activeTabConfig = activeTab !== "__all__" ? PRODUCT_TAB_CONFIG[activeTab] : null;
  const ActiveTabIcon = activeTabConfig?.icon || MapIcon;

  /* ── SSR Modal State ── */
  const [ssrModalOpen, setSSRModalOpen] = useState(false);
  const [ssrFlightComponent, setSSRFlightComponent] = useState(null);
  const [ssrActiveCategory, setSSRActiveCategory] = useState(null);
  const [ssrSearchQuery, setSSRSearchQuery] = useState("");
  const [ssrSelectedLegId, setSSRSelectedLegId] = useState("__all__");
  const [ssrSelectedTravelerId, setSSRSelectedTravelerId] = useState("__all__");
  const [ssrDeleteConfirm, setSSRDeleteConfirm] = useState(null);
  const [ssrPricingEdit, setSSRPricingEdit] = useState(null);
  const [ssrPricingSaving, setSSRPricingSaving] = useState(false);

  /* ── SSR API hooks ── */
  const { execute: addSSRsApi, loading: addingSSR } = useAddBookingSSRs();
  const { execute: deleteSSRApi, loading: deletingSSR } = useDeleteBookingSSR();
  const { execute: updateSSRStatusApi } = useUpdateSSRStatus();
  const [ssrStatusSavingId, setSSRStatusSavingId] = useState(null);
  const { data: apiCategories } = useGetSSRCategories();
  const { data: apiItems } = useGetSSRItems({ status: "active", limit: 500 });

  /* ── SSR category list ── */
  const ssrCategoryList = useMemo(() => {
    const source = (apiCategories && apiCategories.length > 0) ? apiCategories : SSR_CATEGORIES;
    return source.map((c) => {
      const style = SSR_CAT_STYLE[c.code] || {};
      return { id: c.id, code: c.code, name: c.name || style.name, description: c.description || style.description, icon: c.icon || style.icon || "📋", color: c.color || style.color || "#6b7280", bg: style.bg || "#f9fafb" };
    });
  }, [apiCategories]);

  /* ── SSR catalog items ── */
  const ssrCatalog = useMemo(() => (apiItems || []).map((it) => {
    const parts = [];
    if (it.maxWeight != null) parts.push(`Max ${it.maxWeight} kg`);
    if (it.maxSize) parts.push(`Max size ${it.maxSize}`);
    if (it.maxPieces != null) parts.push(`Max ${it.maxPieces} pcs`);
    const unit = it.travelerRequired && it.flightSegmentRequired ? "per person/segment" : it.flightSegmentRequired ? "per segment" : it.travelerRequired ? "per person" : "per item";
    return { id: it.id, code: it.code, label: it.description || it.code, desc: parts.join(" • "), category: it.category, unit };
  }), [apiItems]);

  const getCatStyle = (code) => ssrCategoryList.find((c) => c.code === code) || SSR_CATEGORIES.find((c) => c.code === code) || { icon: "📋", color: "#6b7280", bg: "#f9fafb", name: code };

  /* ── SSR filtered items ── */
  const filteredSSRItems = useMemo(() => {
    let items = ssrCatalog;
    if (ssrActiveCategory) items = items.filter((i) => i.category === ssrActiveCategory);
    if (ssrSearchQuery) {
      const q = ssrSearchQuery.toLowerCase();
      items = items.filter((i) => (i.label || "").toLowerCase().includes(q) || (i.code || "").toLowerCase().includes(q) || (i.desc || "").toLowerCase().includes(q));
    }
    return items;
  }, [ssrActiveCategory, ssrSearchQuery, ssrCatalog]);

  /* ── SSR for the selected flight product ── */
  const ssrFlightServerId = ssrFlightComponent?._raw?.id || null;
  const ssrForFlight = useMemo(() => ssrs.filter((s) => s.productId === ssrFlightServerId), [ssrs, ssrFlightServerId]);
  const ssrCountByCategory = useMemo(() => {
    const map = {};
    ssrForFlight.forEach((s) => { map[s.ssrCategory] = (map[s.ssrCategory] || 0) + 1; });
    return map;
  }, [ssrForFlight]);

  /* ── Open SSR Modal from FlightCard ── */
  const openSSRModal = (flightComponent) => {
    setSSRFlightComponent(flightComponent);
    setSSRActiveCategory(null);
    setSSRSearchQuery("");
    setSSRSelectedLegId("__all__");
    setSSRSelectedTravelerId("__all__");
    setSSRModalOpen(true);
  };

  /* ── Add SSR ── */
  const handleAddSSR = async (item) => {
    if (!bookingId || !ssrFlightServerId) return;
    const travelersToAdd = ssrSelectedTravelerId === "__all__"
      ? travelers
      : [travelers.find((t) => String(t.id) === ssrSelectedTravelerId)].filter(Boolean);
    if (travelersToAdd.length === 0) {
      showToast("No traveler selected", "error");
      return;
    }
    const payloads = travelersToAdd.map((t) => ({
      ssrCode: item.code,
      ssrName: item.label,
      ssrCategory: item.category,
      ssrDescription: item.desc || "",
      quantity: 1,
      travelerId: t.id,
      productId: ssrFlightServerId,
      segmentId: ssrSelectedLegId !== "__all__" ? ssrSelectedLegId : null,
    }));
    const res = await addSSRsApi(bookingId, payloads);
    if (res.success) {
      const tNames = travelersToAdd.map((t) => `${t.firstName || ""} ${t.lastName || ""}`.trim()).join(", ");
      showToast(`SSR "${item.code}" added for ${tNames}`, "success");
      onRefresh?.();
    } else {
      showToast(res.message || "Failed to add SSR", "error");
    }
  };

  /* ── Delete SSR ── */
  const handleDeleteSSR = async (ssr) => {
    if (!bookingId || !ssr.id) return;
    const res = await deleteSSRApi(bookingId, ssr.id);
    if (res.success) {
      showToast("SSR removed successfully", "success");
      setSSRDeleteConfirm(null);
      onRefresh?.();
    } else {
      showToast(res.message || "Failed to remove SSR", "error");
    }
  };

  /* ── Save SSR Pricing ── */
  const handleSaveSSRPricing = async () => {
    if (!ssrPricingEdit || !bookingId || !setSSRPricingApi) return;
    setSSRPricingSaving(true);
    const res = await setSSRPricingApi(bookingId, {
      ssrId: ssrPricingEdit.id,
      costPrice: parseFloat(ssrPricingEdit.costPrice || 0),
      markup: parseFloat(ssrPricingEdit.markup || 0),
      vatTax: parseFloat(ssrPricingEdit.vatTax || 0),
    });
    if (res.success) {
      showToast("SSR pricing saved", "success");
      setSSRPricingEdit(null);
      onRefresh?.();
    } else {
      showToast(res.message || "Failed to save SSR pricing", "error");
    }
    setSSRPricingSaving(false);
  };

  const fmtSSR = (v) => `€ ${parseFloat(v || 0).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // Canonical SSR status colors (Requested/Confirmed/Cancelled/Denied) from the shared map.
  const ssrStatusStyle = (status) => ssrStatusColor(status);

  /* ── Change SSR status (Requested / Confirmed / Cancelled / Denied) ── */
  const handleUpdateSSRStatus = async (ssr, newStatus) => {
    if (!bookingId || !ssr?.id || newStatus === ssr.status) return;
    setSSRStatusSavingId(ssr.id);
    const res = await updateSSRStatusApi(bookingId, ssr.id, newStatus);
    if (res.success) {
      showToast(`SSR status set to ${newStatus}`, "success");
      onRefresh?.();
    } else {
      showToast(res.message || "Failed to update SSR status", "error");
    }
    setSSRStatusSavingId(null);
  };

  /* ── Get traveler label ── */
  const getTravLabel = (travelerId) => {
    const t = travelers.find((tr) => tr.id === travelerId);
    return t ? `${t.firstName || ""} ${(t.lastName || "").toUpperCase()}`.trim() || `Traveler #${travelerId}` : `Traveler #${travelerId}`;
  };

  /* ── Flight legs from selected flight component ── */
  const ssrFlightLegs = useMemo(() => {
    if (!ssrFlightComponent) return [];
    const raw = ssrFlightComponent._raw || {};
    const d = raw.productDetails || {};
    return (d.legs || []).map((leg, idx) => ({
      id: leg.id || String(idx),
      departure: (leg.departureAirport || leg.departure || "").toUpperCase(),
      arrival: (leg.arrivalAirport || leg.arrival || "").toUpperCase(),
      direction: leg.direction,
      airlineCode: leg.airlineCode,
      flightNumber: leg.flightNumber,
    }));
  }, [ssrFlightComponent]);

  const renderCard = (c) => {
    const canc = cancByProduct[c.id];
    switch (c.type) {
      case "Flight":
        return <FlightCard key={c.id} component={c} isLocked={isLocked} onEdit={onEdit} onDelete={onDelete} bookingId={bookingId} onRefresh={onRefresh} airportMap={airportMap} onOpenSSR={openSSRModal} canc={canc} />;
      case "Hotel":
        return <HotelCard key={c.id} component={c} isLocked={isLocked} onEdit={onEdit} onDelete={onDelete} bookingId={bookingId} onRefresh={onRefresh} airportMap={airportMap} canc={canc} />;
      case "Transfer":
        return <TransferCard key={c.id} component={c} isLocked={isLocked} onEdit={onEdit} onDelete={onDelete} bookingId={bookingId} onRefresh={onRefresh} airportMap={airportMap} canc={canc} />;
      default:
        return <GenericProductCard key={c.id} component={c} isLocked={isLocked} onEdit={onEdit} onDelete={onDelete} canc={canc} travelers={travelers} airportMap={airportMap}
          flightPnr={(() => { const fl = (components || []).find((x) => x.type === "Flight"); const fd = fl?._raw?.productDetails || {}; return String(fd.airlinePnr || fd.confirmation?.pnr || fl?._raw?.supplierReference || "").toUpperCase(); })()} />;
    }
  };

  return (
    <div className={styles.itineraryTab}>
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}><MapIcon size={16} /> Itinerary & Components</div>
          <div className={styles.tabSubtitle}>{components.length} component{components.length !== 1 ? "s" : ""} in this booking</div>
        </div>
        {!isLocked && (
          <button className={styles.addBtn} onClick={onAdd}><Plus size={13} /> Add Component</button>
        )}
      </div>

      {/* ── Product Type Tabs ── */}
      {showTabs && (
        <div className={styles.productTabs}>
          {/* All tab */}
          <button
            className={`${styles.productTab} ${styles.tabAll} ${activeTab === "__all__" ? styles.productTabActive : ""}`}
            onClick={() => setActiveTab("__all__")}
          >
            <span className={styles.productTabIcon}><MapIcon size={13} /></span>
            All
            <span className={styles.productTabCount}>{components.length}</span>
          </button>

          {/* Product type tabs */}
          {presentTypes.map((type) => {
            const config = PRODUCT_TAB_CONFIG[type];
            const Icon = config?.icon || FileText;
            const label = config?.label || type;
            const cssClass = config?.cssClass || "tabOther";
            const count = countByType[type] || 0;
            const isActive = activeTab === type;

            return (
              <button
                key={type}
                className={`${styles.productTab} ${styles[cssClass]} ${isActive ? styles.productTabActive : ""}`}
                onClick={() => setActiveTab(type)}
              >
                <span className={styles.productTabIcon}><Icon size={13} /></span>
                {label}
                <span className={styles.productTabCount}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {components.length === 0 && (
        <div className={styles.emptyState}>
          <MapIcon size={28} className={styles.emptyIcon} />
          <div className={styles.emptyTitle}>No components yet</div>
          <div className={styles.emptyText}>Add flight, hotel, transfer, or other travel components to this booking.</div>
          {!isLocked && <button className={styles.addBtn} onClick={onAdd}><Plus size={13} /> Add Component</button>}
        </div>
      )}

      {/* ── Filtered section header (when a specific tab is active) ── */}
      {activeTab !== "__all__" && filteredComponents.length > 0 && (
        <div className={styles.filteredHeader}>
          <span className={styles.filteredTitle}>
            <ActiveTabIcon size={15} />
            {activeTabConfig?.label || activeTab}
          </span>
          <span className={styles.filteredCount}>{filteredComponents.length} item{filteredComponents.length !== 1 ? "s" : ""}</span>
          <span className={styles.filteredDivider} />
        </div>
      )}

      {/* ── Filtered empty state ── */}
      {activeTab !== "__all__" && filteredComponents.length === 0 && components.length > 0 && (
        <div className={styles.emptyState}>
          <ActiveTabIcon size={24} className={styles.emptyIcon} />
          <div className={styles.emptyTitle}>No {activeTabConfig?.label?.toLowerCase() || activeTab.toLowerCase()} in this booking</div>
          <div className={styles.emptyText}>Switch to another tab or add a new component.</div>
        </div>
      )}

      <div className={`${styles.productList} ${activeTab !== "__all__" ? styles.productListAnimated : ""}`} key={activeTab}>
        {filteredComponents.map(renderCard)}
      </div>

      {/* ══════════════════════════════════════════════
          SSR MANAGEMENT MODAL (Flight-centric)
          ══════════════════════════════════════════════ */}
      <BaseModal
        open={ssrModalOpen}
        onClose={() => { setSSRModalOpen(false); setSSRFlightComponent(null); setSSRDeleteConfirm(null); }}
        title={`Manage SSRs — ${(() => {
          if (!ssrFlightComponent) return "";
          const d = ssrFlightComponent._raw?.productDetails || {};
          const legs = d.legs || [];
          const first = legs[0] || {};
          const last = legs[legs.length - 1] || first;
          return first.departure && last.arrival ? `${(first.departure || "").toUpperCase()} → ${(last.arrival || "").toUpperCase()}` : "Flight";
        })()}`}
        width="860px"
        footer={
          <button className={styles.btnSecondary} onClick={() => { setSSRModalOpen(false); setSSRFlightComponent(null); setSSRDeleteConfirm(null); }}>Close</button>
        }
      >
        <div className={styles.ssrModalBody}>
          {/* ── Toolbar: summary pill + search ── */}
          <div className={styles.ssrModalToolbar}>
            <div className={styles.ssrModalSummaryPill}>
              <span className={styles.ssrModalPillNum}>{ssrForFlight.length}</span>
              <span className={styles.ssrModalPillLabel}>services linked</span>
            </div>
            {!isLocked && (
              <div className={styles.ssrModalSearchWrap}>
                <div className={styles.ssrModalSearchBox}>
                  <span className={styles.ssrModalSearchIcon}><SSRSearchIcon /></span>
                  <input
                    className={styles.ssrModalSearchInput}
                    placeholder="Search SSR items..."
                    value={ssrSearchQuery}
                    onChange={(e) => { setSSRSearchQuery(e.target.value); if (e.target.value) setSSRActiveCategory(null); }}
                  />
                  {ssrSearchQuery && (
                    <button className={styles.ssrModalSearchClear} onClick={() => setSSRSearchQuery("")}><SSRXIcon /></button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Context Selector: Traveler + Leg (Flight-centric) ── */}
          {!isLocked && (
            <div className={styles.ssrCtxPanel}>
              {/* Traveler selection */}
              <div className={styles.ssrCtxSection}>
                <div className={styles.ssrCtxSectionHeader}>
                  <span className={styles.ssrCtxSectionIcon}><SSRUserIcon /></span>
                  <span className={styles.ssrCtxSectionTitle}>Traveler</span>
                  <span className={styles.ssrCtxHint}>Select which traveler this SSR applies to</span>
                </div>
                <div className={styles.ssrCtxChipRow}>
                  <button
                    className={`${styles.ssrCtxLegChip} ${ssrSelectedTravelerId === "__all__" ? styles.ssrCtxLegChipActive : ""}`}
                    onClick={() => setSSRSelectedTravelerId("__all__")}
                  >
                    <span className={styles.ssrCtxLegDir}>ALL</span>
                    <span>All Travelers</span>
                  </button>
                  {travelers.map((t) => {
                    const name = `${t.firstName || ""} ${(t.lastName || "").toUpperCase()}`.trim() || `Traveler #${t.id}`;
                    const isActive = ssrSelectedTravelerId === String(t.id);
                    return (
                      <button
                        key={t.id}
                        className={`${styles.ssrCtxLegChip} ${isActive ? styles.ssrCtxLegChipActive : ""}`}
                        onClick={() => setSSRSelectedTravelerId(String(t.id))}
                      >
                        <SSRUserIcon /> <span>{name}</span>
                        {t.type && (
                          <span style={{ fontSize: "10px", fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>
                            {t.type}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Leg / Segment selection */}
              {ssrFlightLegs.length > 0 && (
                <div className={styles.ssrCtxSection}>
                  <div className={styles.ssrCtxSectionHeader}>
                    <span className={styles.ssrCtxSectionIcon}><SSRRouteIcon /></span>
                    <span className={styles.ssrCtxSectionTitle}>Segment / Leg</span>
                    <span className={styles.ssrCtxHint}>Select which leg this SSR applies to</span>
                  </div>
                  <div className={styles.ssrCtxChipRow}>
                    <button
                      className={`${styles.ssrCtxLegChip} ${ssrSelectedLegId === "__all__" ? styles.ssrCtxLegChipActive : ""}`}
                      onClick={() => setSSRSelectedLegId("__all__")}
                    >
                      <span className={styles.ssrCtxLegDir}>ALL</span>
                      <span>All Segments</span>
                    </button>
                    {ssrFlightLegs.map((leg, idx) => {
                      const route = leg.departure && leg.arrival ? `${leg.departure} → ${leg.arrival}` : `Leg ${idx + 1}`;
                      const dirLabel = (dir) => {
                        if (!dir) return "";
                        if (dir === "Outbound") return "OUT";
                        if (dir === "Inbound") return "INB";
                        return dir.substring(0, 3).toUpperCase();
                      };
                      const dir = dirLabel(leg.direction);
                      const isActive = ssrSelectedLegId === leg.id;
                      return (
                        <button
                          key={leg.id}
                          className={`${styles.ssrCtxLegChip} ${isActive ? styles.ssrCtxLegChipActive : ""}`}
                          onClick={() => setSSRSelectedLegId(leg.id)}
                        >
                          {dir && <span className={styles.ssrCtxLegDir}>{dir}</span>}
                          <span>{route}</span>
                          {leg.airlineCode && leg.flightNumber && (
                            <span style={{ fontSize: "10px", fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>
                              {leg.airlineCode}{leg.flightNumber}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Active context bar ── */}
          {!isLocked && (
            <div className={styles.ssrActiveCtxBar}>
              <span className={styles.ssrActiveCtxLabel}>Adding for:</span>
              <span className={styles.ssrActiveCtxChip}>
                <SSRUserIcon /> {ssrSelectedTravelerId === "__all__" ? "All Travelers" : getTravLabel(parseInt(ssrSelectedTravelerId, 10))}
              </span>
              <span className={styles.ssrActiveCtxChip}>
                <SSRPlaneIcon /> {(() => {
                  const legs = ssrFlightLegs;
                  const first = legs[0] || {};
                  const last = legs[legs.length - 1] || first;
                  return first.departure && last.arrival ? `${first.departure} → ${last.arrival}` : "Flight";
                })()}
              </span>
              {ssrSelectedLegId !== "__all__" ? (
                <span className={styles.ssrActiveCtxChip}>
                  <SSRRouteIcon /> {(() => {
                    const leg = ssrFlightLegs.find((l) => l.id === ssrSelectedLegId);
                    return leg && leg.departure && leg.arrival ? `${leg.departure} → ${leg.arrival}` : ssrSelectedLegId;
                  })()}
                </span>
              ) : (
                <span className={styles.ssrActiveCtxChipMuted}>All Segments</span>
              )}
            </div>
          )}

          {/* ── SSR Catalog: Category Grid / Search Results / Category Items ── */}
          {!isLocked && (
            <>
              {ssrSearchQuery ? (
                <div>
                  <div className={styles.ssrSectionHeading}>
                    Search results for &quot;{ssrSearchQuery}&quot; — {filteredSSRItems.length} item{filteredSSRItems.length !== 1 ? "s" : ""}
                  </div>
                  {filteredSSRItems.length === 0 ? (
                    <div className={styles.ssrEmptyState}>No SSR items found matching your search.</div>
                  ) : (
                    <div className={styles.ssrItemList}>
                      {filteredSSRItems.map((item) => {
                        const cat = getCatStyle(item.category);
                        return (
                          <div key={item.id} className={styles.ssrItemRow}>
                            <span className={styles.ssrItemCatIcon} style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}>
                              <SSRCatIcon code={item.category} />
                            </span>
                            <span className={styles.ssrItemCodeBadge} style={{ background: cat.bg, color: cat.color }}>{item.code}</span>
                            <div className={styles.ssrItemBody}>
                              <span className={styles.ssrItemName}>{item.label}</span>
                              {item.desc && <span className={styles.ssrItemDesc}>{item.desc}</span>}
                            </div>
                            <span className={styles.ssrItemUnit}>{item.unit}</span>
                            <button className={styles.ssrItemAddBtn} onClick={() => handleAddSSR(item)} disabled={addingSSR}>
                              <SSRPlusIcon /> Add
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : ssrActiveCategory ? (
                <div>
                  <div className={styles.ssrBreadcrumb}>
                    <button className={styles.ssrBreadcrumbBack} onClick={() => setSSRActiveCategory(null)}>
                      <SSRArrowLeftIcon /> All Categories
                    </button>
                    <span className={styles.ssrBreadcrumbSep}>/</span>
                    <span className={styles.ssrBreadcrumbCurrent} style={{ color: getCatStyle(ssrActiveCategory).color }}>
                      <SSRCatIcon code={ssrActiveCategory} /> {getCatStyle(ssrActiveCategory).name}
                    </span>
                  </div>
                  {filteredSSRItems.length === 0 ? (
                    <div className={styles.ssrEmptyState}>No items in this category.</div>
                  ) : (
                    <div className={styles.ssrItemList}>
                      {filteredSSRItems.map((item) => {
                        const cat = getCatStyle(item.category);
                        return (
                          <div key={item.id} className={styles.ssrItemRow}>
                            <span className={styles.ssrItemCatIcon} style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}>
                              <SSRCatIcon code={item.category} />
                            </span>
                            <span className={styles.ssrItemCodeBadge} style={{ background: cat.bg, color: cat.color }}>{item.code}</span>
                            <div className={styles.ssrItemBody}>
                              <span className={styles.ssrItemName}>{item.label}</span>
                              {item.desc && <span className={styles.ssrItemDesc}>{item.desc}</span>}
                            </div>
                            <span className={styles.ssrItemUnit}>{item.unit}</span>
                            <button className={styles.ssrItemAddBtn} onClick={() => handleAddSSR(item)} disabled={addingSSR}>
                              <SSRPlusIcon /> Add
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className={styles.ssrSectionHeading}>Browse by Category</div>
                  <div className={styles.ssrCatGrid}>
                    {ssrCategoryList.map((cat) => (
                      <button
                        key={cat.code}
                        className={styles.ssrCatCard}
                        onClick={() => setSSRActiveCategory(cat.code)}
                        style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}
                      >
                        <span className={styles.ssrCatIcon}>
                          <SSRCatIcon code={cat.code} />
                        </span>
                        <div className={styles.ssrCatInfo}>
                          <span className={styles.ssrCatLabel}>{cat.name}</span>
                          <span className={styles.ssrCatDesc}>{cat.description}</span>
                        </div>
                        {(ssrCountByCategory[cat.code] || 0) > 0 && (
                          <span className={styles.ssrCatBadge} style={{ background: cat.color }}>{ssrCountByCategory[cat.code]}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Current SSRs for this flight (Selected Services) ── */}
          {ssrForFlight.length > 0 && (
            <div>
              <div className={styles.ssrSectionHeading}>
                Current Services
                <span className={styles.ssrSelectedCount}>{ssrForFlight.length}</span>
              </div>
              <div className={styles.ssrCurrentList}>
                {ssrForFlight.map((ssr) => {
                  const cat = getCatStyle(ssr.ssrCategory);
                  const stStyle = ssrStatusStyle(ssr.status);
                  const isDeleting = ssrDeleteConfirm?.id === ssr.id;
                  return (
                    <div key={ssr.id} className={`${styles.ssrCurrentRow} ${isDeleting ? styles.ssrCurrentRowDeleting : ""}`}>
                      {isDeleting ? (
                        /* ── Inline delete confirmation ── */
                        <div className={styles.ssrDeleteConfirmBar}>
                          <span className={styles.ssrDeleteConfirmText}>
                            Remove <strong>{ssr.ssrCode}</strong>?
                          </span>
                          <div className={styles.ssrDeleteConfirmActions}>
                            <button className={styles.ssrDeleteBtnCancel} onClick={() => setSSRDeleteConfirm(null)}>Cancel</button>
                            <button className={styles.ssrDeleteBtnConfirm} onClick={() => handleDeleteSSR(ssr)} disabled={deletingSSR}>
                              {deletingSSR ? "Removing…" : "Remove"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className={styles.ssrCurrentCatIcon} style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}>
                            <SSRCatIcon code={ssr.ssrCategory} />
                          </span>
                          <span className={styles.ssrCurrentCodeBadge} style={{ background: cat.bg, color: cat.color }}>{ssr.ssrCode}</span>
                          <div className={styles.ssrCurrentBody}>
                            <span className={styles.ssrCurrentLabel}>{ssr.ssrName || ssr.ssrCode}</span>
                            {ssr.ssrDescription && <span className={styles.ssrCurrentDesc}>{ssr.ssrDescription}</span>}
                            <span style={{ fontSize: "10px", color: "#9ca3af" }}>
                              <SSRUserIcon /> {getTravLabel(ssr.travelerId)}
                            </span>
                          </div>
                          <div className={styles.ssrCurrentPriceGroup}>
                            <span className={styles.ssrCurrentPriceChip} title="Cost Price">
                              <span className={styles.ssrCurrentPriceChipLabel}>Cost</span>
                              {fmtSSR(ssr.costPrice)}
                            </span>
                            <span className={styles.ssrCurrentPriceChip} title="Markup">
                              <span className={styles.ssrCurrentPriceChipLabel}>Markup</span>
                              {fmtSSR(ssr.markup)}
                            </span>
                            <span className={`${styles.ssrCurrentPriceChip} ${styles.ssrCurrentPriceChipSell}`} title="Selling Price">
                              <span className={styles.ssrCurrentPriceChipLabel}>Sell</span>
                              {fmtSSR(ssr.sellingPrice)}
                            </span>
                          </div>
                          {ssr.quantity > 1 && (
                            <span className={styles.ssrCurrentQty}>×{ssr.quantity}</span>
                          )}
                          {isLocked ? (
                            <span className={styles.ssrCurrentStatus} style={{ background: stStyle.bg, color: stStyle.color }}>
                              {ssr.status || "Requested"}
                            </span>
                          ) : (
                            <select
                              value={ssr.status || "Requested"}
                              disabled={ssrStatusSavingId === ssr.id}
                              onChange={(e) => handleUpdateSSRStatus(ssr, e.target.value)}
                              title="Change SSR status"
                              style={{ background: stStyle.bg, color: stStyle.color, border: `1px solid ${stStyle.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 6px", cursor: ssrStatusSavingId === ssr.id ? "wait" : "pointer", outline: "none" }}
                            >
                              {SSR_STATUS_VALUES.map((v) => (
                                <option key={v} value={v} style={{ background: "#fff", color: "#111827" }}>{v}</option>
                              ))}
                            </select>
                          )}
                          {!isLocked && (
                            <button
                              className={styles.ssrCurrentEditPrice}
                              onClick={() => setSSRPricingEdit({ id: ssr.id, costPrice: ssr.costPrice, markup: ssr.markup, vatTax: ssr.vatTax })}
                              title="Edit Pricing"
                            >
                              <Pencil size={11} />
                            </button>
                          )}
                          {!isLocked && (
                            <button className={styles.ssrCurrentRemove} onClick={() => setSSRDeleteConfirm(ssr)} title="Remove SSR">
                              <SSRXIcon />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Empty state ── */}
          {ssrForFlight.length === 0 && !ssrSearchQuery && !ssrActiveCategory && (
            <div className={styles.ssrEmptyState}>
              <strong>No SSRs linked yet.</strong> Select a category above to browse available services, or use the search bar.
            </div>
          )}
        </div>
      </BaseModal>

      {/* ── SSR Pricing Edit Modal ── */}
      {ssrPricingEdit && (
        <BaseModal
          open={!!ssrPricingEdit}
          onClose={() => setSSRPricingEdit(null)}
          title="Edit SSR Pricing"
          width="420px"
          footer={
            <>
              <button className={styles.btnSecondary} onClick={() => setSSRPricingEdit(null)}>Cancel</button>
              <button className={styles.btnSuccess} onClick={handleSaveSSRPricing} disabled={ssrPricingSaving}>
                <SSRSaveIcon /> {ssrPricingSaving ? "Saving..." : "Save Pricing"}
              </button>
            </>
          }
        >
          <div className={styles.ssrPricingEditForm}>
            <div className={styles.ssrPricingEditRow}>
              <label className={styles.ssrPricingEditLabel}>Cost Price</label>
              <div className={styles.ssrPricingEditInputWrap}>
                <span className={styles.ssrPricingEditCurrency}>€</span>
                <input
                  type="number"
                  className={styles.ssrPricingEditInput}
                  value={ssrPricingEdit.costPrice}
                  onChange={(e) => setSSRPricingEdit({ ...ssrPricingEdit, costPrice: e.target.value })}
                  onFocus={(e) => e.target.select()}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className={styles.ssrPricingEditRow}>
              <label className={styles.ssrPricingEditLabel}>Markup</label>
              <div className={styles.ssrPricingEditInputWrap}>
                <span className={styles.ssrPricingEditCurrency}>€</span>
                <input
                  type="number"
                  className={styles.ssrPricingEditInput}
                  value={ssrPricingEdit.markup}
                  onChange={(e) => setSSRPricingEdit({ ...ssrPricingEdit, markup: e.target.value })}
                  onFocus={(e) => e.target.select()}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className={styles.ssrPricingEditRow}>
              <label className={styles.ssrPricingEditLabel}>VAT Tax</label>
              <div className={styles.ssrPricingEditInputWrap}>
                <span className={styles.ssrPricingEditCurrency}>€</span>
                <input
                  type="number"
                  className={styles.ssrPricingEditInput}
                  value={ssrPricingEdit.vatTax}
                  onChange={(e) => setSSRPricingEdit({ ...ssrPricingEdit, vatTax: e.target.value })}
                  onFocus={(e) => e.target.select()}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className={styles.ssrPricingEditSummary}>
              <span className={styles.ssrPricingEditSummaryLabel}>Selling Price</span>
              <span className={styles.ssrPricingEditSummaryValue}>
                {fmtSSR(parseFloat(ssrPricingEdit.costPrice || 0) + parseFloat(ssrPricingEdit.markup || 0))}
              </span>
            </div>
          </div>
        </BaseModal>
      )}
    </div>
  );
};

export default ItineraryTab;
